// @ts-nocheck

/** @typedef {import('./types').BundleItem} BundleItem */
/** @typedef {import('./types').BundlePriceStrategy} BundlePriceStrategy */
/** @typedef {import('./types').AddOnLimits} AddOnLimits */
/** @typedef {import('./types').BundleState} BundleState */

const newDate = new Date();
window.currentTime = newDate.getTime();
if (window.oldTime) {
  setTimeout(() => {
    window.location.reload();
  }, 100);
}
window.oldTime = window.currentTime;

/**
 * Bundle state management singleton
 * Handles global state for the bundle builder
 * @type {Object}
 */
const BundleStateManager = {
  /**
   * Initialize the bundle state
   * @returns {void}
   */
  initBundleState() {
    // Initialize the bundle state if it doesn't exist
    if (!window.fsBundleState) {
      /**
       * @type {import('./types').BundleState}
       */
      window.fsBundleState = {
        bundleItems: {},
        addOnItems: {},
        currentQuantity: 0,
        bundleQuantity: 0,
        addOnQuantity: 0,
        addOnLimits: { minimum: 0, maximum: 0 },
        priceStrategy: { strategy: 'fixed_pricing', value: 0 },
        selectedOption: null,
        totalPrice: 0,
        originalPrice: null,
        discountedPrice: null,
        subscribers: new Map(),
        autoUpgradeEnabled: fsbAdditionalSettings.auto_advance_bundle || true,
        cachedBundleOptions: null,
        bundleOptionsElement: document.querySelector('fsb-bundleoptions'),
        allProductsHaveSingleVariant: false,
        productVariantCounts: new Map(),
        currentView: 'products', // Track current view (products or addons)

        /**
         * Updates multiple state properties at once
         * @param {Object} settings - Settings object
         * @param {number} [settings.bundleQuantity] - Bundle quantity
         * @param {AddOnLimits} [settings.addOnLimits] - Add-on limits
         * @param {BundlePriceStrategy} [settings.priceStrategy] - Price strategy
         * @param {string} [settings.selectedOption] - Selected option ID
         */
        setInitialState(settings = {}) {
          let bundleQuantityChanged = false;
          
          if (settings.bundleQuantity !== undefined) {
            bundleQuantityChanged = this.bundleQuantity !== settings.bundleQuantity;
            this.bundleQuantity = settings.bundleQuantity;
          }
          if (settings.addOnLimits) {
            this.addOnLimits = settings.addOnLimits;
          }
          if (settings.priceStrategy) {
            this.priceStrategy = settings.priceStrategy;
          }
          if (settings.selectedOption) {
            this.selectedOption = settings.selectedOption;
          }
          this.cacheBundleOptions();
          try {
            this.notifySubscribers('stateInitialized');
            if (bundleQuantityChanged) {
              this.notifySubscribers('bundleQuantity');
            }
          } catch (e) {
            console.error('Error notifying subscribers:', e);
          }
        },

        /**
         * Caches bundle options to avoid repeated DOM queries
         */
        cacheBundleOptions() {
          this.cachedBundleOptions = Array.from(document.querySelectorAll('.bundle-option'))
            .map((option) => ({
              element: option,
              quantity: parseInt(option.getAttribute('data-quantity'), 10),
              id: option.getAttribute('data-option-id'),
              settings: JSON.parse(option.dataset.option || '{}'),
            }))
            .reduce((uniqueOptions, option) => {
              if (!uniqueOptions.some((uniqueOption) => uniqueOption.id === option.id)) {
                uniqueOptions.push(option);
              }
              return uniqueOptions;
            }, [])
            .sort((a, b) => a.quantity - b.quantity);
        },

        /**
         * Finds the best fitting bundle for the current quantity
         * @returns {Object|null} Best fitting bundle option
         */
        findOptimalBundle() {
          if (!this.cachedBundleOptions) {
            this.cacheBundleOptions();
          }

          // Find the smallest bundle that can fit current quantity
          return this.cachedBundleOptions.find((option) => option.quantity >= this.currentQuantity);
        },

        /**
         * Finds the next larger bundle than current
         * @returns {Object|null} Next larger bundle or null
         */
        findNextBundle() {
          if (!this.cachedBundleOptions) {
            this.cacheBundleOptions();
          }
          return this.cachedBundleOptions.find((option) => option.quantity > this.bundleQuantity);
        },

        /**
         * Tracks variant counts for a product
         * @param {string|number} productId - Product identifier
         * @param {number} variantCount - Number of available variants
         */
        trackProductVariants(productId, variantCount) {
          this.productVariantCounts.set(productId.toString(), variantCount);
        },

        /**
         * Checks if all products have only single variants
         * @returns {boolean} True if all products have single variants
         */
        checkAllProductsHaveSingleVariant() {
          // If no products tracked yet, default to false
          if (this.productVariantCounts.size === 0) {
            this.allProductsHaveSingleVariant = false;
            return false;
          }

          // Check each product's variant count
          for (const variantCount of this.productVariantCounts.values()) {
            // If any product has more than 1 variant, not all products have single variants
            if (variantCount > 1) {
              this.allProductsHaveSingleVariant = false;
              return false;
            }
          }

          // All products have single variants
          this.allProductsHaveSingleVariant = true;
          return true;
        },

        /**
         * Adds an item to the bundle with auto-sizing
         * @param {string} itemKey - Item identifier
         * @param {import('./types').BundleItem} itemData - Item data
         * @returns {boolean} Success status
         */
        addBundleItem(itemKey, itemData) {
          // Check if we need to auto-upgrade
          if (this.currentQuantity >= this.bundleQuantity && this.autoUpgradeEnabled) {
            const nextBundle = this.findNextBundle();
            if (nextBundle && this.selectedOption !== nextBundle.id) {
              this.autoSwitchBundle(nextBundle);
              // After upgrade, check if we can add the item
              if (this.currentQuantity >= this.bundleQuantity) {
                return false;
              }
            } else {
              return false;
            }
          }

          // Add the item
          if (this.bundleItems[itemKey]) {
            this.bundleItems[itemKey].quantity++;
          } else {
            this.bundleItems[itemKey] = {
              ...itemData,
              quantity: 1,
              addedAt: Date.now(),
            };
          }
          this.currentQuantity++;
          if (window.fsbSettings && window.fsbSettings.added_toast_message) {
            Fsb.showToast(window.fsbSettings.added_toast_message);
          }
          this.notifySubscribers('bundleItems');
          return true;
        },

        /**
         * Switches bundle size with UI updates
         * @param {import('./types').BundleOption} bundleOption - Target bundle option
         */
        autoSwitchBundle(bundleOption) {
          // Update state as before...
          this.setInitialState({
            bundleQuantity: bundleOption.quantity,
            selectedOption: bundleOption.id,
            addOnLimits: bundleOption.settings.add_on || { minimum: 0, maximum: 0 },
            priceStrategy: bundleOption.settings.price || { strategy: 'fixed_pricing', value: 0 },
          });

          // Use cached bundle options component and popover
          if (this.bundleOptionsElement) {
            this.bundleOptionsElement.updateBundleDetails(bundleOption.id);
            this.bundleOptionsElement.updateSelectedAttr(bundleOption.element);
            this.bundleOptionsElement.updatePriceVisibility(bundleOption.settings.price?.strategy);
            if (this.popoverEl) {
              this.bundleOptionsElement.updatePopoverText(bundleOption);
            }
          }

          // Create and show notification (could also be debounced/reused)
          const direction = bundleOption.quantity > this.bundleQuantity ? 'increased' : 'decreased';
          const notification = document.createElement('div');
          notification.className = 'fsb__notification fsb__notification--resize';
          notification.textContent = `Bundle size ${direction} to ${bundleOption.quantity} items`;
          document.querySelector('.fsb-wrapper')?.appendChild(notification);
          setTimeout(() => notification.remove(), 3000);

          // Dispatch event as before
          const bundleOptionChangeEvent = new CustomEvent('bundleOptionChange', {
            detail: {
              addOnLimits: this.addOnLimits,
              bundleQuantity: this.bundleQuantity,
              selectedOptionId: bundleOption.id,
              priceStrategy: this.priceStrategy,
              isAutomatic: true,
            },
            bubbles: true,
            composed: true,
          });
          document.dispatchEvent(bundleOptionChangeEvent);
        },

        /**
         * Removes a bundle item and handles auto-downsizing
         * @param {string} itemKey - Item identifier
         */
        removeBundleItem(itemKey) {
          if (!this.bundleItems[itemKey]) return;

          // Remove the item
          this.bundleItems[itemKey].quantity--;
          if (this.bundleItems[itemKey].quantity === 0) {
            delete this.bundleItems[itemKey];
          }
          this.currentQuantity--;

          // Auto-adjust bundle size if enabled
          // Only downsize if the current bundle size is insufficient for the current quantity
          // This preserves the user's selection when possible
          if (this.autoUpgradeEnabled && this.currentQuantity > 0) {
            // Check if current bundle size is still adequate
            if (this.currentQuantity > this.bundleQuantity) {
              // Need to upgrade to a larger bundle
              const nextBundle = this.findNextBundle();
              if (nextBundle && nextBundle.id !== this.selectedOption) {
                this.autoSwitchBundle(nextBundle);
              }
            }
            // Don't downsize unless absolutely necessary - preserve user's choice
          }

          this.notifySubscribers('bundleItems');
        },

        /**
         * Adds an add-on item to the bundle
         * @param {string} variantId - Variant ID
         * @param {import('./types').AddOnItem} itemData - Item data
         * @returns {string} Generated unique key for the item
         */
        addAddonItem(variantId, itemData) {
          const key = variantId.toString();
          // Check if we need to update an existing item or add a new one
          if (this.addOnItems[key]) {
            // If item already exists, update its quantity
            const existingQuantity = this.addOnItems[key].quantity || 0;
            const newQuantity = itemData.quantity || 1;
            const quantityDiff = newQuantity - existingQuantity;
            
            this.addOnItems[key] = {
              ...itemData,
              variantId, // Store the original variantId
              quantity: newQuantity,
            };
            // Update total quantity
            this.addOnQuantity += quantityDiff;
          } else {
            // Add as a new item with the specified quantity or default to 1
            this.addOnItems[key] = {
              ...itemData,
              variantId, // Store the original variantId
              quantity: itemData.quantity || 1,
            };
            // Update total quantity
            this.addOnQuantity += itemData.quantity || 1;
          }
          if (window.fsbSettings && window.fsbSettings.added_toast_message) {
            Fsb.showToast(window.fsbSettings.added_toast_message);
          }
          
          this.notifySubscribers('addOnItems');
          return key; // Return the key used for the item
        },

        /**
         * Removes an add-on item from the bundle
         * @param {string} itemKey - Item identifier
         */
        removeAddonItem(itemKey) {
          if (this.addOnItems[itemKey]) {
            delete this.addOnItems[itemKey];
            this.addOnQuantity--;
            this.notifySubscribers('addOnItems');
          }
        },

        /**
         * Notifies subscribers of state changes
         * @param {string} changeType - Type of change
         */
        notifySubscribers(changeType) {
          for (const [component, callback] of this.subscribers) {
            if (typeof callback === 'function') {
              try {
                callback.call(component, changeType);
              } catch (e) {
                console.error('Error calling subscriber callback:', e);
              }
            }
          }
          // Update progress bar whenever bundle items change
          if (changeType === 'bundleItems' || changeType === 'stateInitialized') {
            this.updateProgressBar();
          }
          
          // Update box quantity display whenever bundle quantity changes
          if (changeType === 'bundleItems' || changeType === 'stateInitialized' || changeType === 'bundleQuantity') {
            this.updateBoxQuantityDisplay();
          }
          
          // Update all product card capacity states when bundle items, add-on items, or bundle quantity changes
          if (changeType === 'bundleItems' || changeType === 'addOnItems' || changeType === 'stateInitialized' || changeType === 'bundleQuantity') {
            this.updateAllCardCapacityStates();
          }

          // Update selection counts when state is initialized or items change
          if (changeType === 'bundleItems' || changeType === 'addOnItems' || changeType === 'stateInitialized' || changeType === 'bundleQuantity') {
            this.updateSelectionCounts();
          }
        },

        /**
         * Updates selection counts in the UI
         */
        updateSelectionCounts() {
          // Update bundle item counts (current/total)
          const bundleCountElements = document.querySelectorAll('.bundle-selection-count.products .count');
          bundleCountElements.forEach(element => {
            element.textContent = this.currentQuantity;
          });

          // Addon count is set once during initialization and doesn't need dynamic updates
          // It shows the maximum available addon slots
        },

        /**
         * Updates the progress bar based on current selection
         */
        updateProgressBar() {
          const progressBars = document.querySelectorAll('.fsb__progress-bar-fill');
          const progressPercentage = this.bundleQuantity > 0 ? Math.min((this.currentQuantity / this.bundleQuantity) * 100, 100) : 0;
          
          progressBars.forEach(progressBar => {
            progressBar.style.width = `${progressPercentage}%`;
            progressBar.setAttribute('data-progress', progressPercentage.toString());
          });
        },

        /**
         * Calculates the number of boxes needed based on selected bundle quantity
         * Uses the minimum bundle option as the base box size
         */
        calculateBoxQuantity() {
          // Get the minimum bundle option quantity as the base box size
          const minBundleOption = this.findMinimumBundleOption();
          const itemsPerBox = minBundleOption ? minBundleOption.quantity : 12; // fallback to 12
          
          if (this.bundleQuantity === 0) {
            return 1; // Default to 1 box minimum
          }
          return Math.ceil(this.bundleQuantity / itemsPerBox);
        },

        /**
         * Finds the minimum bundle option quantity
         * @returns {Object|null} The bundle option with the smallest quantity
         */
        findMinimumBundleOption() {
          if (!this.cachedBundleOptions || this.cachedBundleOptions.length === 0) {
            return null;
          }
          return this.cachedBundleOptions.reduce((min, option) => {
            return option.quantity < min.quantity ? option : min;
          });
        },

        /**
         * Updates the box quantity display
         */
        updateBoxQuantityDisplay() {
          const boxQuantity = this.calculateBoxQuantity();
          const boxQuantityElements = document.querySelectorAll('.box-quantity');
          
          boxQuantityElements.forEach(element => {
            element.textContent = boxQuantity;
          });
        },

        /**
         * Updates all product card capacity states
         */
        updateAllCardCapacityStates() {
          const productCards = document.querySelectorAll('fsb-productcard');
          productCards.forEach(card => {
            if (typeof card.updateCardSelectionState === 'function') {
              card.updateCardSelectionState();
            }
          });
        },

        /**
         * Iterates over each addon container
         * @param {function} callback - Function to call for each container
         */
        forEachAddonContainer(callback) {
          const containers = document.querySelectorAll('.addon-items-sidebar-container');
          containers.forEach(callback);
        },

        /**
         * Adds an addon item to the DOM
         * @param {string} itemKey - Item identifier
         * @param {Object} itemData - Item data
         * @param {HTMLElement} container - Container element
         */
        addAddonItemToDOM(itemKey, itemData, container) {
          // Find the template anywhere in the document (not just in this container)
          const template = document.querySelector('[data-bundle-item-template]');
          let itemElement;

          if (template) {
            // Clone ONLY the .bundle-item element, not the wrapper
            const templateBundleItem = template.querySelector('.bundle-item');
            if (templateBundleItem) {
              itemElement = templateBundleItem.cloneNode(true);
              // Remove any template-related attributes
              itemElement.removeAttribute('data-bundle-item-template');
            }
          }

          // If no template or template doesn't have .bundle-item, create from scratch
          if (!itemElement) {
            itemElement = this.createBundleItemElement(itemKey, itemData);
          }

          if (!itemElement) return;

          // Populate the element with item data
          this.populateBundleItemElement(itemElement, itemKey, itemData);

          // Add the bundle-item directly to the container (no wrapper!)
          container.appendChild(itemElement);
        },

        /**
         * Creates a bundle item element from scratch
         * @param {string} itemKey - Item identifier
         * @param {Object} itemData - Item data
         * @returns {HTMLElement} Created bundle item element
         */
        createBundleItemElement(itemKey, itemData) {
          const itemElement = document.createElement('div');
          itemElement.className = 'bundle-item';
          
          itemElement.innerHTML = `
            <div class="bundle-item__image">
              <img src="${itemData.image || ''}" alt="${itemData.name || ''}" />
            </div>
            <div class="bundle-item__details">
              <div class="bundle-item__name">${itemData.name || ''}</div>
              <div class="bundle-item__controls">
                <fsb-quantityselector class="fsb__quantity-selector">
                  <button type="button" class="fsb__quantity-selector-button fsb__quantity-selector-decrease" aria-label="Decrease quantity">
                    <span>-</span>
                  </button>
                  <input type="number" class="fsb__quantity-selector-input" value="${itemData.quantity || 1}" min="0" data-variant-id="${itemData.variantId || itemKey}" aria-label="Product quantity">
                  <button type="button" class="fsb__quantity-selector-button fsb__quantity-selector-increase" aria-label="Increase quantity">
                    <span>+</span>
                  </button>
                </fsb-quantityselector>
                <button class="bundle-item__remove" data-variant-id="${itemData.variantId || itemKey}" aria-label="Remove item">
                  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="27" viewBox="0 0 26 27" fill="none">
                    <path d="M18.5969 10.4367L18.3239 18.6753C18.3006 19.381 18.0025 20.0497 17.4927 20.5398C16.9829 21.0297 16.3016 21.3025 15.5935 21.3H10.4058C9.6981 21.3025 9.01724 21.0301 8.50754 20.5407C7.99783 20.0512 7.69938 19.3832 7.67542 18.678L7.40239 10.4367C7.39641 10.2562 7.4626 10.0807 7.58638 9.9489C7.71017 9.81707 7.88142 9.73966 8.06246 9.73371C8.24349 9.72776 8.41949 9.79373 8.55172 9.91714C8.68396 10.0405 8.7616 10.2113 8.76758 10.3917L9.04062 18.6324C9.05421 18.9843 9.20403 19.3171 9.45859 19.5611C9.71314 19.8051 10.0527 19.9414 10.4058 19.941H15.5935C15.9471 19.9413 16.2871 19.8048 16.5416 19.5601C16.7963 19.3156 16.9458 18.982 16.9588 18.6298L17.2318 10.3917C17.2377 10.2113 17.3153 10.0405 17.4476 9.91714C17.5798 9.79373 17.7558 9.72776 17.9369 9.73371C18.1178 9.73966 18.2892 9.81707 18.4129 9.9489C18.5367 10.0807 18.6029 10.2562 18.5969 10.4367ZM19.5 7.69496C19.5 7.87544 19.4281 8.04852 19.3001 8.17614C19.172 8.30375 18.9985 8.37544 18.8174 8.37544H7.18259C7.00155 8.37544 6.82794 8.30375 6.69993 8.17614C6.57192 8.04852 6.5 7.87544 6.5 7.69496C6.5 7.51448 6.57192 7.3414 6.69993 7.21378C6.82794 7.08617 7.00155 7.01447 7.18259 7.01447H9.29864C9.51491 7.01505 9.72368 6.9354 9.88429 6.79099C10.0449 6.6466 10.1459 6.44779 10.1676 6.23327C10.2179 5.73004 10.4544 5.26352 10.831 4.92462C11.2075 4.58572 11.6971 4.39871 12.2044 4.40003H13.7948C14.3022 4.39871 14.7918 4.58572 15.1683 4.92462C15.5449 5.26352 15.7813 5.73004 15.8318 6.23327C15.8535 6.44779 15.9544 6.6466 16.1151 6.79099C16.2756 6.9354 16.4844 7.01505 16.7007 7.01447H18.8167C18.9978 7.01447 19.1714 7.08617 19.2994 7.21378C19.4275 7.3414 19.5 7.51448 19.5 7.69496ZM11.3526 7.01447H14.6481C14.5584 6.81014 14.4998 6.59364 14.4741 6.37209C14.4572 6.20435 14.3784 6.04885 14.2531 5.9357C14.1276 5.82254 13.9646 5.75981 13.7956 5.75965H12.2051C12.036 5.75981 11.873 5.82254 11.7476 5.9357C11.6223 6.04885 11.5435 6.20435 11.5266 6.37209C11.5007 6.59368 11.4425 6.81018 11.3526 7.01447ZM12.0399 17.3246V11.5302C12.0399 11.3497 11.968 11.1766 11.84 11.049C11.712 10.9214 11.5384 10.8497 11.3573 10.8497C11.1763 10.8497 11.0027 10.9214 10.8747 11.049C10.7467 11.1766 10.6747 11.3497 10.6747 11.5302V17.3273C10.6747 17.5078 10.7467 17.6808 10.8747 17.8085C11.0027 17.9361 11.1763 18.0078 11.3573 18.0078C11.5384 18.0078 11.712 17.9361 11.84 17.8085C11.968 17.6808 12.0399 17.5078 12.0399 17.3273V17.3246ZM15.326 17.3246V11.5302C15.326 11.3497 15.2541 11.1766 15.126 11.049C14.998 10.9214 14.8244 10.8497 14.6433 10.8497C14.4624 10.8497 14.2887 10.9214 14.1606 11.049C14.0327 11.1766 13.9607 11.3497 13.9607 11.5302V17.3273C13.9607 17.5078 14.0327 17.6808 14.1606 17.8085C14.2887 17.9361 14.4624 18.0078 14.6433 18.0078C14.8244 18.0078 14.998 17.9361 15.126 17.8085C15.2541 17.6808 15.326 17.5078 15.326 17.3273V17.3246Z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
            </div>
          `;
          
          return itemElement;
        },

        /**
         * Populates a bundle item element with data
         * @param {HTMLElement} itemElement - Element to populate
         * @param {string} itemKey - Item identifier
         * @param {Object} itemData - Item data
         */
        populateBundleItemElement(itemElement, itemKey, itemData) {
          // Update name
          const nameElement = itemElement.querySelector('.bundle-item__name');
          if (nameElement) nameElement.textContent = itemData.name || '';

          // Update image
          const imageElement = itemElement.querySelector('.bundle-item__image img');
          if (imageElement && itemData.image) {
            imageElement.src = itemData.image;
            imageElement.alt = itemData.name || '';
          }

          // Update quantity selector
          const quantitySelector = itemElement.querySelector('.fsb__quantity-selector-input');
          if (quantitySelector) {
            quantitySelector.value = itemData.quantity || 1;
            quantitySelector.setAttribute('data-variant-id', itemData.variantId || itemKey);
          }

          // Update remove button
          const removeButton = itemElement.querySelector('.bundle-item__remove');
          if (removeButton) {
            removeButton.setAttribute('data-variant-id', itemData.variantId || itemKey);
          }
        },



        /**
         * Updates selection counts in the UI
         */
        updateSelectionCounts() {
          // Update bundle item counts
          const bundleCountElements = document.querySelectorAll('.bundle-selection-count.products .count');
          bundleCountElements.forEach(element => {
            element.textContent = this.currentQuantity;
          });

          // Addon count is set once during initialization and doesn't need dynamic updates
        },

        /**
         * Subscribes a component to state changes
         * @param {Object} component - Component to subscribe
         * @param {function(string):void} callback - Callback function
         */
        subscribe(component, callback) {
          if (typeof callback === 'function') {
            this.subscribers.set(component, callback);
          }
        },

        /**
         * Unsubscribes a component from state changes
         * @param {Object} component - Component to unsubscribe
         */
        unsubscribe(component) {
          this.subscribers.delete(component);
        }
      };
    }
    // Store reference to the global state
    this.bundleState = window.fsBundleState;
  }
};

var Fsb = Fsb || {};

// ---------------------------------------------------------------------------
// Money format handler
Fsb.formatMoney = (cents, format = fsMoneyFormat) => {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  const currencyRate = window.Shopify?.currency?.rate ?? 1;
  cents *= currencyRate;
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    const [dollars, cents] = number.split('.');
    const dollarsAmount = dollars.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    const centsAmount = cents ? `${decimal}${cents}` : '';
    return dollarsAmount + centsAmount;
  };
  let value = '';
  const formatKey = format.match(placeholderRegex)?.[1];
  switch (formatKey) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', '.');
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ');
      break;
    case 'amount_no_decimals_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 0, "'");
      break;
  }
  return format.replace(placeholderRegex, value);
};

Fsb.showToast = (message) => {
  // Remove any existing toast
  let toast = document.querySelector('.fsb__toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'fsb__toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  toast.classList.add('fsb__toast--visible');

  // Remove after 2.5s
  clearTimeout(Fsb._toastTimeout);
  Fsb._toastTimeout = setTimeout(() => {
    toast.classList.remove('fsb__toast--visible');
  }, 1000);
};

window.Fsb = Fsb;

/**
 * Bundle options component
 * @customElement
 * @extends HTMLElement
 */
class FsbBundleOptions extends HTMLElement {
  /**
   * Constructor
   */
  constructor() {
    super();
    this.initializeProperties();
  }

  /**
   * Called when element is connected to the DOM
   */
  connectedCallback() {
    this.initializeElements();
    this.initializeBundleState();
    this.initializePopover();
    this.addEventListeners();
  }

  /**
   * Initialize component properties
   */
  initializeProperties() {
    /**
     * @type {import('./types').BundleState}
     */
    this.bundleState = null;
  }

  initializeElements() {
    this.bundleOptions = Array.from(this.querySelectorAll('.bundle-option'));
    this.selectedBundleOption = this.querySelector('.bundle-option[selected]');
    this.popoverEl = this.querySelector('fsb-popover');
    this.aside = this.closest('fsb-aside');
    BundleStateManager.initBundleState.call(this);
    this.bundleState.subscribe(this, this.handleStateChange);
  }

  initializePopover() {
    const popover = this.querySelector('fsb-popover');
    if (popover) {
      const trigger = popover.querySelector('.fsb__popover-trigger');
      if (trigger && this.selectedBundleOption) {
        trigger.innerHTML = this.selectedBundleOption.innerHTML;
      }
    }
  }

  addEventListeners() {
    this.bundleOptions.forEach((bundleOption) => {
      bundleOption.addEventListener('click', (event) => {
        this.handleBundleOptionChange(bundleOption);
        const popover = this.querySelector('fsb-popover');
        if (popover) {
          popover.close();
          event.stopPropagation(); // Prevent the popover from reopening
        }
      });
    });
  }

  handleStateChange(changeType) {
    if (changeType === 'stateInitialized') {
      // Find the currently selected bundle option based on state
      const selectedId = this.bundleState.selectedOption;
      if (selectedId) {
        const newSelectedOption = this.querySelector(`.bundle-option[data-option-id="${selectedId}"]`);
        if (newSelectedOption && !newSelectedOption.hasAttribute('selected')) {
          this.updatePopoverText(newSelectedOption);
          this.updateSelectedAttr(newSelectedOption);
        }
      }
    }
  }

  initializeBundleState() {
    if (this.selectedBundleOption) {
      const bundleVariantId = this.selectedBundleOption.getAttribute('data-option-id');
      const optionSettings = JSON.parse(this.selectedBundleOption.dataset.option || '{}');
      this.bundleState.setInitialState({
        bundleQuantity: parseInt(this.selectedBundleOption.getAttribute('data-quantity'), 10) || 0,
        selectedOption: bundleVariantId,
        addOnLimits: optionSettings.add_on || { minimum: 0, maximum: 0 },
        priceStrategy: optionSettings.price || { strategy: 'fixed_pricing', value: 0 },
      });
      // Update UI to reflect initial state
      this.updateBundleDetails(bundleVariantId);
      this.updateSelectedAttr(this.selectedBundleOption);
      this.updatePriceVisibility(this.bundleState.priceStrategy.strategy);
    }
  }

  updateBundleState(bundleOption) {
    const bundleVariantId = bundleOption.getAttribute('data-option-id');
    this.updateWindowUrl(bundleVariantId);
    this.updateBundleDetails(bundleVariantId);
    this.updateSelectedAttr(bundleOption);
    this.updatePopoverText(bundleOption);
    this.bundleQuantity = parseInt(bundleOption.getAttribute('data-quantity'), 10) || 0;
    const optionSettings = JSON.parse(bundleOption.dataset.option || '{}');
    const newAddOnLimits = optionSettings.add_on || { minimum: 0, maximum: 0 };
    const newPriceStrategy = optionSettings.price || { strategy: 'fixed_pricing', value: 0 };
    this.updatePriceVisibility(newPriceStrategy.strategy);
    return { newAddOnLimits, newPriceStrategy, bundleVariantId };
  }

  handleBundleOptionChange(bundleOption) {
    const bundleVariantId = bundleOption.getAttribute('data-option-id');
    const optionSettings = JSON.parse(bundleOption.dataset.option || '{}');
    this.bundleState.setInitialState({
      bundleQuantity: parseInt(bundleOption.getAttribute('data-quantity'), 10) || 0,
      selectedOption: bundleVariantId,
      addOnLimits: optionSettings.add_on || { minimum: 0, maximum: 0 },
      priceStrategy: optionSettings.price || { strategy: 'fixed_pricing', value: 0 },
    });
    this.updatePopoverText(bundleOption);
    this.updateBundleDetails(bundleVariantId);
    this.updateSelectedAttr(bundleOption);
    // Update progress bar after bundle option change
    this.bundleState.updateProgressBar();
    
    // Update "What's Included" section
    this.updateWhatsIncluded(optionSettings);
    
    const bundleOptionChangeEvent = new CustomEvent('bundleOptionChange', {
      detail: {
        addOnLimits: this.bundleState.addOnLimits,
        bundleQuantity: this.bundleState.bundleQuantity,
        selectedOptionId: bundleVariantId,
        priceStrategy: this.bundleState.priceStrategy,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(bundleOptionChangeEvent);
  }

  updatePopoverText(bundleOption) {
    const popover = this.querySelector('fsb-popover');
    if (!popover) return;

    const trigger = popover.querySelector('.fsb__popover-trigger');
    if (!trigger) return;

    // If the option has HTML content, use it directly
    if (bundleOption.innerHTML.trim()) {
      trigger.innerHTML = bundleOption.innerHTML;
    } else {
      // Fallback to textContent if no HTML
      trigger.textContent = bundleOption.textContent;
    }

    // Force repaint to ensure UI update
    trigger.style.display = 'none';
    trigger.offsetHeight;
    trigger.style.display = '';
  }

  updatePriceVisibility(priceStrategy) {
    const tabsContainer = document.querySelector('.fsb__tabs-container');
    if (tabsContainer) {
      if (priceStrategy === 'fixed_pricing') {
        tabsContainer.classList.add('fsb__price-hidden');
      } else {
        tabsContainer.classList.remove('fsb__price-hidden');
      }
    }
  }

  updateWindowUrl(variantId) {
    const url = new URL(window.location.href);
    variantId ? url.searchParams.set('variant', variantId) : url.searchParams.delete('variant');
    window.history.replaceState({}, '', url.toString());
  }

  updateBundleDetails(bundleVariantId) {
    if (!this.aside) return;
    this.aside.querySelectorAll('.bundle-details, .bundle__additional-info').forEach((option) => {
      option.classList.add('hidden');
    });
    this.aside
      .querySelectorAll(
        `.bundle-details[data-option-id="${bundleVariantId}"],.bundle__additional-info[data-option-id="${bundleVariantId}"]`
      )
      .forEach((activeOption) => {
        activeOption.classList.remove('hidden');
      });
  }

  updateSelectedAttr(bundleOption) {
    this.bundleOptions.forEach((bundle) => {
      bundle.removeAttribute('selected');
    });
    bundleOption.setAttribute('selected', 'selected');
  }

  updateWhatsIncluded(optionSettings) {
    const whatsIncludedContent = document.getElementById('whats-included-content');
    if (!whatsIncludedContent) return;

    if (optionSettings && optionSettings.additional_info && optionSettings.additional_info.length > 0) {
      let html = '';
      optionSettings.additional_info.forEach(function(item) {
        html += '<div class="included-item">';
        html += '<span class="included-key">' + item.key + '</span>';
        html += '<span class="included-value">' + item.value + '</span>';
        html += '</div>';
      });
      whatsIncludedContent.innerHTML = html;
    } else {
      const isFrench = document.documentElement.lang === 'fr' || (window.Shopify && window.Shopify.locale === 'fr');
      const fallbackText = isFrench ? 'Boîte régulière' : 'Regular Box';
      whatsIncludedContent.innerHTML = '<span><span class="box-quantity">1</span>x ' + fallbackText + '</span>';
    }
  }

  removeExcessItems() {
    while (this.bundleState.currentQuantity > this.bundleState.bundleQuantity) {
      const variantId = [...this.aside.bundleItems].pop();
      if (variantId) {
        const variantRemovedEvent = new CustomEvent('variantRemovedFromBundle', {
          detail: { variantId },
          bubbles: true,
          composed: true,
        });
        document.dispatchEvent(variantRemovedEvent);
      }
    }
  }
}

customElements.define('fsb-bundleoptions', FsbBundleOptions);

/**
 * Product variant selector component
 * Handles variant selection and option combinations
 */
class FsVariantSelector extends HTMLElement {
  constructor() {
    super();
    this.currentVariant = {};
    this.variants = [];
    this.handleChange = this.handleChange.bind(this);
  }

  connectedCallback() {
    this.variants = this.getJSONFromScript('data-all-variants') || [];
    this.initializeProperties();
    this.addEventListener('change', this.handleChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
  }

  initializeProperties() {
    this.currentVariant = this.getVariantData();
    this.selectInitialVariant();
    this.updateOptionVisibility();
  }

  getJSONFromScript(dataAttribute) {
    try {
      const script = this.querySelector(`script[${dataAttribute}]`);
      if (!script) {
        console.warn(`No script element found with ${dataAttribute}`);
        return null;
      }

      const content = script.textContent.trim();

      if (!content || content === '[]' || content === '[        ]') {
        return [];
      }

      if (typeof content === 'object') {
        return content;
      }

      const cleanedContent = content.replace(/,(\s*[\]}])/g, '$1');

      try {
        const parsed = JSON.parse(cleanedContent);

        if (Array.isArray(parsed)) {
          return parsed.filter((item) => item && typeof item === 'object');
        } else if (typeof parsed === 'object') {
          return parsed;
        }

        console.warn('Parsed content has unexpected format');
        return null;
      } catch (parseError) {
        throw parseError;
      }
    } catch (error) {
      console.error('Error parsing variant data:', error);

      try {
        const script = this.querySelector(`script[${dataAttribute}]`);
        if (!script) return null;

        const content = script.textContent
          .replace(/^\uFEFF/, '')
          .replace(/\s+/g, ' ')
          .trim();

        return JSON.parse(content);
      } catch (recoveryError) {
        console.error('Recovery failed, returning empty result');
        return null;
      }
    }
  }

  handleChange(event) {
    const changedElement = event.target;
    const optionIndex = parseInt(changedElement.dataset.optionIndex);
    if (changedElement.type === 'radio') {
      const relatedSelect = this.querySelector(`select[data-option-index="${optionIndex}"]`);
      if (relatedSelect) relatedSelect.value = changedElement.value;
    } else if (changedElement.tagName === 'SELECT') {
      const relatedRadios = this.querySelectorAll(`input[type="radio"][data-option-index="${optionIndex}"]`);
      relatedRadios.forEach((radio) => {
        radio.checked = radio.value === changedElement.value;
      });
    }
    if (optionIndex === 0) {
      this.resetSubsequentOptions();
    }
    this.updateOptionVisibility();
    this.currentVariant = this.getVariantData();
    const variantChangeEvent = new CustomEvent('variantChange', {
      detail: { variant: this.currentVariant },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(variantChangeEvent);
  }

  resetSubsequentOptions() {
    const selects = this.querySelectorAll('select');
    const firstOptionValue = selects[0].value;
    const matchingVariants = this.variants.filter(
      (variant) => variant.available && variant.options[0] === firstOptionValue
    );
    for (let i = 1; i < selects.length; i++) {
      const availableValues = new Set(matchingVariants.map((v) => v.options[i]));
      const firstAvailable = Array.from(availableValues)[0];
      if (firstAvailable) {
        selects[i].value = firstAvailable;
        const radios = this.querySelectorAll(`input[type="radio"][data-option-index="${i}"]`);
        radios.forEach((radio) => {
          radio.checked = radio.value === firstAvailable;
        });
      }
    }
  }

  updateOptionVisibility() {
    const selects = Array.from(this.querySelectorAll('select'));
    const currentSelections = selects.map((select) => select.value);
    selects.length > 0 ? this.enableAllOptionsForSelect(selects[0]) : {};
    for (let i = 1; i < selects.length; i++) {
      const availableValues = this.getAvailableValues(i, currentSelections);
      const select = selects[i];
      Array.from(select.options).forEach((option) => {
        if (option.value) {
          const isAvailable = availableValues.has(option.value);
          option.disabled = !isAvailable;
          option.classList.toggle('unavailable', !isAvailable);
        }
      });
      const radios = this.querySelectorAll(`input[type="radio"][data-option-index="${i}"]`);
      radios.forEach((radio) => {
        const isAvailable = availableValues.has(radio.value);
        radio.disabled = !isAvailable;
        const swatch = radio.nextElementSibling;
        if (swatch && swatch.classList.contains('color-swatch')) {
          swatch.classList.toggle('unavailable', !isAvailable);
        }
      });
    }
  }

  enableAllOptionsForSelect(select) {
    Array.from(select.options).forEach((option) => {
      option.disabled = false;
      option.classList.remove('unavailable');
    });
    const radios = this.querySelectorAll(`input[type="radio"][data-option-index="${select.dataset.optionIndex}"]`);
    radios.forEach((radio) => {
      radio.disabled = false;
      const swatch = radio.nextElementSibling;
      if (swatch && swatch.classList.contains('color-swatch')) {
        swatch.classList.remove('unavailable');
      }
    });
  }

  getAvailableValues(optionIndex, currentSelections) {
    const matchingVariants = this.variants.filter((variant) => {
      return (
        variant.available &&
        currentSelections.every((selected, idx) => idx >= optionIndex || variant.options[idx] === selected)
      );
    });
    return new Set(matchingVariants.map((variant) => variant.options[optionIndex]));
  }

  getVariantData() {
    const selectedOptions = this.selectedOptionValues;
    if (selectedOptions.length === 0) {
      return this.variants[0];
    }
    return this.variants.find((variant) => variant.options.every((option, index) => option === selectedOptions[index]));
  }

  selectInitialVariant() {
    if (this.variants.length > 1) {
      const firstAvailableVariant = this.currentVariant || this.variants.find((variant) => variant.available);
      if (firstAvailableVariant) {
        const selects = this.querySelectorAll('select');
        const radios = this.querySelectorAll('input[type="radio"]');
        selects.forEach((select) => {
          const option = select.querySelector(
            `option[value="${firstAvailableVariant.options[select.dataset.optionIndex]}"]`
          );
          if (option) {
            option.selected = true;
          }
        });
        radios.forEach((radio) => {
          if (radio.value === firstAvailableVariant.options[radio.dataset.optionIndex]) {
            radio.checked = true;
          }
        });
        if (selects.length > 0) {
          this.currentVariant = firstAvailableVariant;
          const variantChangeEvent = new CustomEvent('variantChange', {
            detail: { variant: firstAvailableVariant },
            bubbles: true,
            composed: true,
          });
          this.dispatchEvent(variantChangeEvent);
        }
      }
    }
  }

  get selectedOptionValues() {
    return [...this.querySelectorAll('select')].map((select) => select.value);
  }
}

customElements.define('fsb-variantselector', FsVariantSelector);

/**
 * Tab navigation component
 * Manages product category tabs
 */
class FsbTabs extends HTMLElement {
  constructor() {
    super();
    this.navLinks = [];
    this.contentItems = [];
    this.hasAllTab = false;
    this.isSingleCategory = false;
    this.currentView = 'products'; // Default view
  }

  connectedCallback() {
    this.navLinks = this.querySelectorAll('.tabs__navigation-link');
    this.contentItems = this.querySelectorAll('.tabs__content-item');
    this.hasAllTab = this.navLinks[0]?.classList.contains('tabs__navigation-link--all');
    this.isSingleCategory = this.contentItems.length === 1;

    // Initialize view-specific class on the component
    this.classList.add('fsb-view--products');

    // Initialize bundle state reference
    BundleStateManager.initBundleState.call(this);
    if (this.bundleState) {
      this.bundleState.subscribe(this, this.handleStateChange);
    }

    this.render();
    if (!this.isSingleCategory) {
      this.addEventListeners();
    }
  }

  disconnectedCallback() {
    if (this.bundleState) {
      this.bundleState.unsubscribe(this);
    }
  }

  /**
   * Handle state changes from the bundle state
   * @param {string} changeType - Type of change
   */
  handleStateChange(changeType) {
    if (changeType === 'view_changed') {
      this.updateViewClasses();
    }
  }

  render() {
    if (this.isSingleCategory) {
      this.contentItems[0].classList.add('active');
      return;
    }
    if (!this.navLinks.length || !this.contentItems.length) {
      console.error('Required elements not found in FsbTabs');
      return;
    }
    this.navLinks[0].classList.add('active');
    if (this.hasAllTab) {
      this.contentItems.forEach((item) => item.classList.add('active'));
    } else {
      this.contentItems[0].classList.add('active');
    }
    if (!this.hasAllTab) {
      this.contentItems.forEach((item, index) => {
        if (index !== 0) item.classList.remove('active');
      });
    }
  }

  addEventListeners() {
    this.navLinks.forEach((link, index) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.activateTab(link, index);
      });
    });
  }

  activateTab(link, index) {
    this.navLinks.forEach((link) => link.classList.remove('active'));
    link.classList.add('active');
    if (this.hasAllTab && index === 0) {
      this.contentItems.forEach((item) => item.classList.add('active'));
    } else {
      this.contentItems.forEach((item, itemIndex) => {
        if (this.hasAllTab) {
          item.classList.toggle('active', itemIndex === index - 1);
        } else {
          item.classList.toggle('active', itemIndex === index);
        }
      });
    }

    // Check if this is an add-ons tab and update the bundle state
    const tabContent = this.querySelector(`[aria-labelledby="${link.id}"]`);
    const linkHref = link.getAttribute('href');
    const isAddonTab = linkHref === '#addons' || link.classList.contains('tabs__navigation-link--addons') || 
                       (tabContent && tabContent.id === 'addons') ||
                       (tabContent && tabContent.dataset.categoryName && tabContent.dataset.categoryName.toLowerCase() === 'add-ons');
    
    console.log('Tab switch detected - href:', linkHref, 'isAddonTab:', isAddonTab);
    
    if (this.bundleState) {
      this.bundleState.currentView = isAddonTab ? 'addons' : 'products';
      this.bundleState.notifySubscribers('view_changed');
    } else if (window.fsBundleState) {
      window.fsBundleState.currentView = isAddonTab ? 'addons' : 'products';
      window.fsBundleState.notifySubscribers('view_changed');
    }

    // Update view classes directly as well
    this.currentView = isAddonTab ? 'addons' : 'products';
    this.updateViewClasses();

    // Update dropdown state in sidebar based on current view
    const sidebar = document.querySelector('fsb-aside');
    if (sidebar && typeof sidebar.setDropdownState === 'function') {
      sidebar.setDropdownState(!isAddonTab); // Enable dropdown for products view, disable for addons view
    }
  }

  /**
   * Update CSS classes based on current view
   */
  updateViewClasses() {
    const currentView = this.getCurrentView();

    // Remove all view-related classes
    this.classList.remove('fsb-view--products', 'fsb-view--addons');

    // Add class for current view
    this.classList.add(`fsb-view--${currentView}`);

    // Also add class to container for easier styling
    const container = this.closest('.fsb');
    if (container) {
      container.classList.remove('fsb--products-view', 'fsb--addons-view');
      container.classList.add(`fsb--${currentView}-view`);
    }
  }

  // Helper method to get the current view
  getCurrentView() {
    return this.bundleState.currentView;
  }

  isProductsView() {
    return this.getCurrentView() === 'products';
  }

  isAddonsView() {
    return this.getCurrentView() === 'addons';
  }
}

customElements.define('fsb-tabs', FsbTabs);
/**
 * Quantity selector component
 * Handles product quantity selection in bundle
 */
class FsbQuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.decreaseBtn = null;
    this.increaseBtn = null;
    this.input = null;
    this.productCard = null;
    this.minValue = 0;
  }

  connectedCallback() {
    this.decreaseBtn = this.querySelector('.fsb__quantity-selector-decrease');
    this.increaseBtn = this.querySelector('.fsb__quantity-selector-increase');
    this.input = this.querySelector('.fsb__quantity-selector-input');
    this.productCard = this.closest('fsb-productcard');

    if (!this.productCard) {
      console.warn('FsbQuantitySelector: No parent product card found');
      return;
    }

    // Set initial value
    if (!this.input.value || this.input.value === '0') {
      this.input.value = this.minValue.toString();
    }

    // Don't disable decrease button - allow removal via decrease
    // The decrease button should always be enabled to allow item removal

    // Initialize with current state from bundle
    this.syncWithBundleState();
  }

  syncWithBundleState() {
    if (!this.productCard?.currentVariant?.id) return;

    const variantId = this.productCard.currentVariant.id;
    const bundleState = this.productCard.bundleState;
    const isAddon = this.productCard.isAddon;

    if (!bundleState) return;

    // Count quantity based on item type (addon or bundle)
    let count = 0;
    if (isAddon) {
      // For addon items
      const key = variantId.toString();
      const addonItem = bundleState.addOnItems[key];
      if (addonItem) {
        count = addonItem.quantity || 0;
      }
    } else {
      // For bundle items
      for (const key in bundleState.bundleItems) {
        const item = bundleState.bundleItems[key];
        if (item.variantId == variantId) {
          count += item.quantity || 0;
        }
      }
    }

    // Update input and button states
    this.input.value = count;
    
    // Don't disable decrease button - allow removal via decrease
    this.decreaseBtn.disabled = false;

    // Check capacity based on item type
    if (isAddon) {
      // For addon items - check against addon capacity
      const isAtAddonCapacity = bundleState.addOnQuantity >= bundleState.addOnLimits.maximum;
      this.increaseBtn.disabled = isAtAddonCapacity;
    } else {
      // For bundle items - check against bundle capacity
      const canAutoUpgrade = bundleState.findNextBundle() !== null;
      const isAtCapacity = bundleState.currentQuantity >= bundleState.bundleQuantity && !canAutoUpgrade;
      this.increaseBtn.disabled = isAtCapacity;
    }
  }
}

customElements.define('fsb-quantityselector', FsbQuantitySelector);

/**
 * Product card component
 * @customElement
 * @extends HTMLElement
 */
class FsbProductCard extends HTMLElement {
  /**
   * Constructor
   */
  constructor() {
    super();
    /**
     * @type {import('./types').BundleState}
     */
    this.bundleState = null;
    this.bindEvents();
  }

  bindEvents() {
    // Bind all event handlers to the instance
    this.handleAddToKit = this.handleAddToKit.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleStateChange = this.handleStateChange.bind(this);
    this.handleQuantityDecrease = this.handleQuantityDecrease.bind(this);
    this.handleQuantityIncrease = this.handleQuantityIncrease.bind(this);
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
  }

  connectedCallback() {
    this.isAddon = this.getAttribute('is') === 'fsb-productaddon' || 
                   this.dataset.category === 'add-ons' ||
                   this.closest('.fsb__addons-container') !== null;
    
    console.log('=== FsbProductCard connectedCallback ===');
    console.log('getAttribute("is"):', this.getAttribute('is'));
    console.log('dataset.category:', this.dataset.category);
    console.log('closest(".fsb__addons-container"):', this.closest('.fsb__addons-container'));
    console.log('isAddon result:', this.isAddon);
    console.log('product title:', this.dataset.product ? JSON.parse(this.dataset.product).title : 'no product data');

    BundleStateManager.initBundleState.call(this);
    this.bundleState.subscribe(this, this.handleStateChange);
    this.button = this.querySelector('.fsb__atk-button--add');
    this.product = JSON.parse(this.dataset.product || '{}');
    this.category = this.dataset.category;
    this.variantSelector = this.querySelector('fsb-variantselector');
    this.currentVariant = this.variantSelector?.currentVariant;
    
    // Set the data-variant-id attribute to ensure the sidebar can find this card
    if (this.currentVariant && this.currentVariant.id) {
      this.setAttribute('data-variant-id', this.currentVariant.id);
    }
    this.aside = document.querySelector('fsb-aside');
    this.priceElement = this.querySelector('#product-price');
    this.quantitySelector = this.querySelector('fsb-quantityselector');

    if (this.variantSelector?.variants.length === 0 && window.fsbSettings.hide_unavailable_products) {
      this.hideProduct();
      return;
    }

    this.addEventListeners();
    this.initializeProductDetails();

    // Initialize quantity selector for both regular products and add-ons
    if (this.quantitySelector) {
      this.initQuantitySelector();
    }
    if (this.bundleState && this.product.id && !this.isAddon) {
      const variantSelector = this.querySelector('fsb-variantselector');
      if (variantSelector && variantSelector.variants) {
        const variantCount = variantSelector.variants.length;
        this.bundleState.trackProductVariants(this.product.id, variantCount);
      }
    }

    this.checkAndUpdateButtonState(this.button);
    
    // Initialize card capacity state
    this.updateCardSelectionState();
  }

  disconnectedCallback() {
    if (this.bundleState) {
      this.bundleState.unsubscribe(this);
    }

    // Remove event listeners
    if (this.button) {
      this.button.removeEventListener('click', this.handleAddToKit);
    }

    this.removeEventListener('variantChange', this.handleVariantChange);

    // Remove quantity selector event listeners
    if (this.quantitySelector) {
      const decreaseBtn = this.quantitySelector.querySelector('.fsb__quantity-selector-decrease');
      const increaseBtn = this.quantitySelector.querySelector('.fsb__quantity-selector-increase');
      const inputField = this.quantitySelector.querySelector('.fsb__quantity-selector-input');

      decreaseBtn?.removeEventListener('click', this.handleQuantityDecrease);
      increaseBtn?.removeEventListener('click', this.handleQuantityIncrease);
      inputField?.removeEventListener('change', this.handleQuantityChange);
    }
  }

  handleStateChange(changeType) {
    switch (changeType) {
      case 'bundleItems':
        if (this.quantitySelector && !this.isAddon) {
          this.updateQuantityValue();
          this.updateQuantityButtonStates();
        }
        this.checkAndUpdateButtonState(this.button);
        this.updateCardSelectionState();
        break;
      case 'addOnItems':
        if (this.isAddon) {
          this.updateQuantityValue();
          this.updateQuantityButtonStates();
          this.checkAndUpdateButtonState(this.button);
        }
        this.updateCardSelectionState();
        break;
      case 'bundleQuantity':
        this.checkAndUpdateButtonState(this.button);
        break;
      case 'stateInitialized':
        this.checkAndUpdateButtonState(this.button);
        if (this.quantitySelector) {
          this.updateQuantityValue();
          this.updateQuantityButtonStates();
        }
        this.updateCardSelectionState();
        break;
    }
  }

  hideProduct() {
    this.setAttribute('hidden', '');
    if (this.button) {
      this.button.removeEventListener('click', this.handleAddToKit);
    }
    this.removeEventListener('variantChange', this.handleVariantChange);
  }

  addEventListeners() {
    if (this.button) {
      this.button.addEventListener('click', this.handleAddToKit);
    }

    this.addEventListener('variantChange', this.handleVariantChange);

    if (this.quantitySelector) {
      const decreaseBtn = this.quantitySelector.querySelector('.fsb__quantity-selector-decrease');
      const increaseBtn = this.quantitySelector.querySelector('.fsb__quantity-selector-increase');
      const inputField = this.quantitySelector.querySelector('.fsb__quantity-selector-input');

      decreaseBtn?.addEventListener('click', this.handleQuantityDecrease);
      increaseBtn?.addEventListener('click', this.handleQuantityIncrease);
      inputField?.addEventListener('change', this.handleQuantityChange);
    }
  }

  handleVariantChange(event) {
    this.updateProductDetails(event);
    this.checkAndUpdateButtonState(this.button);

    // Update data-variant-id attribute when variant changes to ensure sidebar can find this card
    if (event.detail && event.detail.variant && event.detail.variant.id) {
      this.setAttribute('data-variant-id', event.detail.variant.id);
    }

    if (this.quantitySelector) {
      this.updateQuantityValue();
      this.updateQuantityButtonStates();
    }
    
    // Update card capacity state when variant changes
    this.updateCardSelectionState();
    if (this.bundleState && this.product.id && !this.isAddon) {
      const variantSelector = this.querySelector('fsb-variantselector');
      if (variantSelector && variantSelector.variants) {
        const variantCount = variantSelector.variants.length;
        this.bundleState.trackProductVariants(this.product.id, variantCount);
      }
    }
  }

  checkAndUpdateButtonState(button = this.button) {
    if (!button) return;

    const variant = this.currentVariant;
    const available = this.isVariantAvailable(variant);
    button.setAttribute('data-available', available);

    if (this.isAddon) {
      // For add-ons, check against add-on limits
      if (this.bundleState.addOnQuantity >= this.bundleState.addOnLimits.maximum) {
        button.setAttribute('disabled', '');
      } else if (variant?.id && available) {
        button.removeAttribute('disabled');
      } else {
        button.setAttribute('disabled', '');
      }
    } else {
      // For regular bundle items, check against bundle size
      const canAutoUpgrade = this.bundleState.findNextBundle() != undefined;
      const isAtCapacity = this.bundleState.currentQuantity >= this.bundleState.bundleQuantity && !canAutoUpgrade;
      if (isAtCapacity) {
        button.setAttribute('disabled', '');
        // Also disable quantity increase button if present
        this.quantitySelector?.querySelector('.fsb__quantity-selector-increase')?.setAttribute('disabled', '');
      } else if (variant?.id && available) {
        button.removeAttribute('disabled');
        // Enable quantity increase button if present
        this.quantitySelector?.querySelector('.fsb__quantity-selector-increase')?.removeAttribute('disabled');
      } else {
        button.setAttribute('disabled', '');
        // Disable quantity increase button if present
        this.quantitySelector?.querySelector('.fsb__quantity-selector-increase')?.setAttribute('disabled', '');
      }
    }
  }

  isVariantAvailable(variant) {
    if (this.variantSelector && this.variantSelector.selectedOptionValues.some((value) => !value)) {
      return 'unset';
    }
    if (!variant) return false;

    const inventoryPolicy = variant.inventory_policy;
    const inventoryMgmt = variant.inventory_management;
    const inventoryQuantity = variant.inventory_quantity || 0;

    if (!inventoryMgmt || inventoryPolicy === 'continue') {
      return variant.available;
    }

    if (inventoryPolicy === 'deny') {
      const totalBundleQuantity = Object.values(this.bundleState.bundleItems)
        .filter((item) => item.variantId === variant.id)
        .reduce((total, item) => total + item.quantity, 0);

      let totalAddonQuantity = 0;
      if (this.isAddon) {
        totalAddonQuantity = Object.values(this.bundleState.addOnItems)
          .filter((item) => item.variantId === variant.id)
          .reduce((total, item) => total + item.quantity, 0);
      }

      const totalUsedQuantity = totalBundleQuantity + totalAddonQuantity;

      return variant.available && inventoryQuantity > totalUsedQuantity;
    }

    return variant.available;
  }

  initializeProductDetails() {
    if (this.currentVariant) {
      this.updateProductDetails({
        detail: { variant: this.currentVariant, firstLoad: true },
      });
    }
    if (this.variantSelector?.variants[0]?.bundlePrice) {
      this.updateProductDetails({
        detail: { variant: this.variantSelector.variants[0], firstLoad: true },
      });
    }
  }

  handleAddToKit() {
    console.log('=== handleAddToKit called ===');
    console.log('this.isAddon:', this.isAddon);
    console.log('product title:', this.product?.title);
    
    const variant = this.currentVariant;
    if (!variant?.id || !this.bundleState) return;

    if (this.isAddon) {
      console.log('Taking ADDON path');
      // Add-on product logic - use same pattern as bundle products
      if (this.quantitySelector) {
        // First add with quantity 1 if not already in the bundle
        if (this.getAddonQuantityInBundle(variant.id) === 0) {
          const addonData = {
            price: 0, // Addon products should be free
            title: this.product.title.split(' - ')[0],
            variantTitle: !variant.is_default ? variant.title : null,
            image: variant.media_link || this.product.featured_image,
            metafields: this.product.metafields, // Include metafields
            containsNuts: this.dataset.containsNuts === 'true', // Add nuts info from data attribute
            variantId: variant.id,
            quantity: 1
          };
          
          // Add the item with quantity 1
          this.bundleState.addAddonItem(variant.id.toString(), addonData);
          
          // Show quantity selector and update UI
          this.quantitySelector.classList.remove('hidden');
          this.updateQuantityValue();
          this.updateQuantityButtonStates();
        } else {
          // If already in bundle, just increase quantity
          this.handleQuantityIncrease();
        }
        return;
      }

      // Fallback for non-quantity addon products (though shouldn't happen now)
      if (this.bundleState.addOnQuantity >= this.bundleState.addOnLimits.maximum) {
        const notification = document.createElement('div');
        notification.className = 'fsb__notification';
        notification.textContent = window.fsbSettings.personalization_max_error;
        this.appendChild(notification);
        setTimeout(() => notification.remove(), 3000);
        return;
      }

      // Add addon item with unique key
      const addonData = {
        price: 0, // Addon products should be free
        title: this.product.title.split(' - ')[0],
        variantTitle: !variant.is_default ? variant.title : null,
        image: variant.media_link || this.product.featured_image,
        metafields: this.product.metafields, // Include metafields
        containsNuts: this.dataset.containsNuts === 'true', // Add nuts info from data attribute
        quantity: 1
      };
      this.bundleState.addAddonItem(variant.id.toString(), addonData);
    } else {
      console.log('Taking BUNDLE path');
      // If we have a quantity selector, use the increment functionality
      if (this.quantitySelector) {
        // If not already in bundle, add with quantity 1 first
        if (this.getVariantQuantityInBundle(variant.id) === 0) {
          this.bundleState.addBundleItem(variant.id, {
            price: variant.bundlePrice,
            title: this.product.title.split(' - ')[0],
            variantTitle: !variant.is_default ? variant.title : null,
            image: variant.media_link || this.product.featured_image,
            variantId: variant.id,
            quantity: 1
          });
          
          // Show quantity selector and update UI
          this.quantitySelector.classList.remove('hidden');
          this.updateQuantityValue();
          this.updateQuantityButtonStates();
        } else {
          // If already in bundle, just increase quantity
          this.handleQuantityIncrease();
        }
        return;
      }

      // Standard add to kit logic for non-quantity products
      if (this.bundleState.currentQuantity >= this.bundleState.bundleQuantity) {
        this.showNotification(window.fsbSettings.personalization_max_error || 'Maximum bundle size reached');
      } else {
        this.bundleState.addBundleItem(variant.id, {
          price: variant.bundlePrice,
          title: this.product.title.split(' - ')[0],
          variantTitle: !variant.is_default ? variant.title : null,
          image: variant.media_link || this.product.featured_image,
          variantId: variant.id,
        });
      }
    }
  }

  updateProductDetails({ detail: { variant, firstLoad = false } }) {
    this.currentVariant = variant;
    if (!variant) return;

    const priceElement = this.querySelector('#product-price');
    const imageElement = this.querySelector('.fsb__product-card__image');

    if (priceElement && variant.bundlePrice !== undefined) {
      priceElement.innerHTML =
        variant.bundlePrice === 0 ? window.fsbSettings.free_label || 'Free' : Fsb.formatMoney(variant.bundlePrice);
    }

    if (firstLoad === true) {
      return;
    }

    if (imageElement && variant.media_link) {
      imageElement.src = `${variant.media_link.replace('=450', '=50')}`;
      imageElement.src = `${variant.media_link}`;
      imageElement.removeAttribute('srcset');
      imageElement.alt = variant.media_alt || this.product.title.split(' - ')[0] || '';
    } else if (firstLoad === false && imageElement && this.product.featured_image) {
      imageElement.src = `${this.product.featured_image}&width=450`;
    }
  }

  // Quantity selector methods
  initQuantitySelector() {
    if (!this.quantitySelector) return;

    // Initialize to current variant quantity
    this.updateQuantityValue();
    this.updateQuantityButtonStates();
    
    // Show/hide quantity selector based on current quantity
    const currentQuantity = this.isAddon 
      ? this.getAddonQuantityInBundle(this.currentVariant?.id)
      : this.getVariantQuantityInBundle(this.currentVariant?.id);
      
    if (currentQuantity === 0) {
      this.quantitySelector.classList.add('hidden');
    } else {
      this.quantitySelector.classList.remove('hidden');
    }
  }

  updateQuantityValue() {
    if (!this.quantitySelector) return;

    const variantId = this.currentVariant?.id;
    const quantityInput = this.quantitySelector.querySelector('.fsb__quantity-selector-input');

    if (quantityInput && variantId) {
      if (this.isAddon) {
        const currentQuantity = this.getAddonQuantityInBundle(variantId);
        quantityInput.value = currentQuantity;
      } else {
        const currentQuantity = this.getVariantQuantityInBundle(variantId);
        quantityInput.value = currentQuantity;
      }
    }
  }

  getVariantQuantityInBundle(variantId) {
    if (!variantId || !this.bundleState) return 0;

    let count = 0;

    // Count all matching variants in the bundle state
    // Note: variantId might be stored as string or number, so we check both
    for (const key in this.bundleState.bundleItems) {
      const item = this.bundleState.bundleItems[key];
      if (item.variantId == variantId) {
        // Intentional loose comparison
        count += item.quantity;
      }
    }

    return count;
  }

  updateQuantityButtonStates() {
    if (!this.quantitySelector) return;

    const decreaseBtn = this.quantitySelector.querySelector('.fsb__quantity-selector-decrease');
    const increaseBtn = this.quantitySelector.querySelector('.fsb__quantity-selector-increase');
    const quantityInput = this.quantitySelector.querySelector('.fsb__quantity-selector-input');

    if (!decreaseBtn || !increaseBtn || !quantityInput) return;

    // Allow decrease button to work when quantity is 1 (to enable removal)
    const currentValue = parseInt(quantityInput.value, 10) || 0;
    decreaseBtn.disabled = false; // Always allow decrease to enable removal

    if (this.isAddon) {
      // Addon product logic
      const isAtAddonCapacity = this.bundleState.addOnQuantity >= this.bundleState.addOnLimits.maximum;
      const variantNotAvailable = !this.isVariantAvailable(this.currentVariant);
      
      increaseBtn.disabled = isAtAddonCapacity || variantNotAvailable;
      
      // Add visual feedback for disabled state
      if (increaseBtn.disabled) {
        increaseBtn.setAttribute('title', isAtAddonCapacity ? 'Addon limit reached' : 'Variant not available');
      } else {
        increaseBtn.removeAttribute('title');
      }

      // Show/hide quantity selector using same logic as bundle items
      if (this.getAddonQuantityInBundle(this.currentVariant?.id) === 0) {
        this.quantitySelector.classList.add('hidden');
      } else {
        this.quantitySelector.classList.remove('hidden');
      }
    } else {
      // Bundle product logic
      const isAtCapacity = this.bundleState.currentQuantity >= this.bundleState.bundleQuantity;
      const variantNotAvailable = !this.isVariantAvailable(this.currentVariant);
      
      increaseBtn.disabled = isAtCapacity || variantNotAvailable;
      
      // Add visual feedback for disabled state
      if (increaseBtn.disabled) {
        increaseBtn.setAttribute('title', isAtCapacity ? 'Bundle is at maximum capacity' : 'Variant not available');
      } else {
        increaseBtn.removeAttribute('title');
      }

      if (this.getVariantQuantityInBundle(this.currentVariant?.id) === 0) {
        this.quantitySelector.classList.add('hidden');
      } else {
        this.quantitySelector.classList.remove('hidden');
      }
    }
  }

  handleQuantityDecrease() {
    if (!this.quantitySelector || !this.currentVariant) return;

    const quantityInput = this.quantitySelector.querySelector('.fsb__quantity-selector-input');
    const variantId = this.currentVariant.id;
    const currentValue = parseInt(quantityInput.value, 10) || 0;

    // Always allow decrease - when quantity is 0, set to 0 to trigger removal
    const newValue = Math.max(0, currentValue - 1);
    quantityInput.value = newValue;

    if (this.isAddon) {
      // Handle addon quantity decrease (0 will trigger removal)
      this.updateAddonQuantityInBundle(variantId, newValue);
    } else {
      // Handle bundle quantity decrease (0 will trigger removal)
      this.updateQuantityInBundle(variantId, newValue);
    }

    this.updateQuantityButtonStates();
  }

  handleQuantityIncrease() {
    if (!this.quantitySelector || !this.currentVariant) return;

    const quantityInput = this.quantitySelector.querySelector('.fsb__quantity-selector-input');
    const variantId = this.currentVariant.id;
    const currentValue = parseInt(quantityInput.value, 10) || 0;

    if (this.isAddon) {
      // Check if we can add more addons - prevent if at addon capacity
      if (this.bundleState.addOnQuantity >= this.bundleState.addOnLimits.maximum) {
        Fsb.showToast('Addon limit reached');
        return;
      }

      // Update input value
      const newValue = currentValue + 1;
      quantityInput.value = newValue;

      // Add one addon item
      this.updateAddonQuantityInBundle(variantId, newValue);
    } else {
      // Check if we can add more - prevent if at capacity
      if (this.bundleState.currentQuantity >= this.bundleState.bundleQuantity) {
        Fsb.showToast(window.fsbSettings.personalization_max_error || 'Maximum bundle size reached');
        return;
      }

      // Update input value
      const newValue = currentValue + 1;
      quantityInput.value = newValue;

      // Add one item to bundle
      this.updateQuantityInBundle(variantId, newValue);
    }
    
    this.updateQuantityButtonStates();
  }

  handleQuantityChange(event) {
    if (!this.quantitySelector || !this.currentVariant) return;

    const quantityInput = event.target;
    const variantId = this.currentVariant.id;
    let newValue = parseInt(quantityInput.value, 10);

    // Enforce minimum value of 0
    if (isNaN(newValue) || newValue < 0) {
      newValue = 0;
      quantityInput.value = newValue;
      return;
    }

    if (this.isAddon) {
      // Handle addon quantity change - mirroring bundle item logic exactly
      const currentQuantity = this.getAddonQuantityInBundle(variantId);
      const otherAddonsTotal = Object.entries(this.bundleState.addOnItems)
        .reduce((total, [id, item]) => {
          return id !== variantId.toString() ? total + (item.quantity || 0) : total;
        }, 0);

      // Calculate how many we can add
      const remainingSpace = this.bundleState.addOnLimits.maximum - otherAddonsTotal;
      const maxAllowed = Math.max(0, remainingSpace);

      if (newValue > maxAllowed) {
        // Immediately set to max allowed
        newValue = maxAllowed;
        quantityInput.value = maxAllowed;
        Fsb.showToast('Addon limit reached');
      }
      
      // Always update the addon quantity immediately
      this.updateAddonQuantityInBundle(variantId, newValue);
    } else {
      // Handle bundle quantity change
      const totalBundle = this.bundleState.currentQuantity;
      const currentCount = this.bundleState.bundleItems[variantId]?.quantity || 0;
      const otherItems = totalBundle - currentCount;
      const maxAllowed = this.bundleState.bundleQuantity - otherItems;

      // Show message if exceeding max, but allow the input to show the entered value temporarily
      if (newValue > maxAllowed) {
        Fsb.showToast('Maximum bundle size reached');
        // Update to max allowed after a short delay to show the user their input
        setTimeout(() => {
          quantityInput.value = maxAllowed;
          this.updateQuantityInBundle(variantId, maxAllowed);
        }, 100);
        return;
      }

      // Update bundle with new quantity
      this.updateQuantityInBundle(variantId, newValue);
    }
    
    this.updateQuantityButtonStates();
  }

  updateQuantityInBundle(variantId, newQuantity) {
    if (!variantId || !(this.bundleState || window.fsBundleState)) return;

    // Get the actual bundle state to use
    const bundleState = this.bundleState || window.fsBundleState;
    
    // Get current quantity for this variant
    const currentQuantity = bundleState.bundleItems[variantId]?.quantity || 0;

    // If no change needed, return early
    if (newQuantity === currentQuantity) return;

    // Clear existing items for this variant
    if (bundleState.bundleItems[variantId]) {
      delete bundleState.bundleItems[variantId];
      bundleState.currentQuantity -= currentQuantity;
    }

    // If new quantity is greater than 0, add the items
    if (newQuantity > 0) {
      const itemData = {
        price: this.currentVariant.bundlePrice,
        title: this.product.title.split(' - ')[0],
        variantTitle: !this.currentVariant.is_default ? this.currentVariant.title : null,
        image: this.currentVariant.media_link || this.product.featured_image,
        variantId: this.currentVariant.id,
        metafields: this.product.metafields,
        containsNuts: this.dataset.containsNuts === 'true',
        quantity: newQuantity
      };

      bundleState.bundleItems[variantId] = {
        ...itemData,
        addedAt: Date.now()
      };
      bundleState.currentQuantity += newQuantity;
    }

    // Sync with sidebar quantity selectors
    this.syncQuantityWithSidebar(variantId, newQuantity);

    // Notify subscribers of the change
    bundleState.notifySubscribers('bundleItems');
  }

  updateAddonQuantityInBundle(variantId, newQuantity) {
    if (!variantId || !(this.bundleState || window.fsBundleState)) return;

    // Get the actual bundle state to use
    const bundleState = this.bundleState || window.fsBundleState;

    // Get current quantity for this variant
    const currentQuantity = bundleState.addOnItems[variantId]?.quantity || 0;

    // If no change needed, return early
    if (newQuantity === currentQuantity) return;

    // First remove the current item to update state
    if (bundleState.addOnItems[variantId]) {
      delete bundleState.addOnItems[variantId];
      bundleState.addOnQuantity -= currentQuantity;
    }

    // If new quantity is greater than 0, add the item
    if (newQuantity > 0) {
      const itemData = {
        price: 0, // Addon products should be free
        title: this.product.title.split(' - ')[0],
        variantTitle: !this.currentVariant.is_default ? this.currentVariant.title : null,
        image: this.currentVariant.media_link || this.product.featured_image,
        metafields: this.product.metafields,
        containsNuts: this.dataset.containsNuts === 'true',
        variantId: variantId,
        quantity: newQuantity
      };

      // Add the item with the correct quantity
      bundleState.addAddonItem(variantId.toString(), itemData);
      
      // Make sure we don't exceed maximum (the addAddonItem method now handles quantity tracking)
      if (bundleState.addOnQuantity > bundleState.addOnLimits.maximum) {
        const maxAllowed = Math.max(0, bundleState.addOnLimits.maximum - (bundleState.addOnQuantity - newQuantity));
        itemData.quantity = maxAllowed;
        bundleState.addAddonItem(variantId.toString(), itemData);
      }
    }
    
    // Notify subscribers of the change
    bundleState.notifySubscribers('addOnItems');
    
    // Sync with sidebar quantity selectors
    this.syncQuantityWithSidebar(variantId, newQuantity);
    
    // Update addon dots after quantity change
    const sidebarComponent = document.querySelector('fsb-aside');
    if (sidebarComponent && sidebarComponent.updateAddonDots) {
      sidebarComponent.updateAddonDots();
    }
  }

  /**
   * Syncs quantity between product card and sidebar
   * @param {string|number} variantId - The variant ID
   * @param {number} newQuantity - The new quantity
   */
  syncQuantityWithSidebar(variantId, newQuantity) {
    // Update sidebar quantity selectors - try multiple selector patterns
    const sidebarQuantityInputs = document.querySelectorAll(`
      .bundle-item[data-variant-id="${variantId}"] .fsb__quantity-selector-input,
      .bundle-item[data-item-key="${variantId}"] .fsb__quantity-selector-input,
      .bundle-item-container[data-variant-id="${variantId}"] .fsb__quantity-selector-input,
      .bundle-item-container[data-item-key="${variantId}"] .fsb__quantity-selector-input
    `);
    
    sidebarQuantityInputs.forEach(input => {
      if (parseInt(input.value, 10) !== newQuantity) {
        input.value = newQuantity;
        
        // Update button states
        const container = input.closest('.fsb__quantity-selector');
        if (container) {
          const decreaseBtn = container.querySelector('.fsb__quantity-selector-decrease');
          const increaseBtn = container.querySelector('.fsb__quantity-selector-increase');
          
          if (decreaseBtn) {
            decreaseBtn.disabled = false; // Always allow decrease for removal
          }
          
          if (increaseBtn) {
            const bundleState = this.bundleState || window.fsBundleState;
            if (this.isAddon) {
              increaseBtn.disabled = bundleState.addOnQuantity >= bundleState.addOnLimits.maximum;
            } else {
              increaseBtn.disabled = bundleState.currentQuantity >= bundleState.bundleQuantity;
            }
          }
        }
      }
    });
    
    // Also sync with other product cards that have the same variant
    const otherProductCards = document.querySelectorAll(`fsb-productcard[data-variant-id="${variantId}"], fsb-productaddon[data-variant-id="${variantId}"]`);
    otherProductCards.forEach(card => {
      if (card !== this) {
        const quantityInput = card.querySelector('.fsb__quantity-selector-input');
        if (quantityInput && parseInt(quantityInput.value, 10) !== newQuantity) {
          quantityInput.value = newQuantity;
          
          // Update button states on the other card
          const container = quantityInput.closest('.fsb__quantity-selector');
          if (container) {
            const decreaseBtn = container.querySelector('.fsb__quantity-selector-decrease');
            const increaseBtn = container.querySelector('.fsb__quantity-selector-increase');
            
            if (decreaseBtn) {
              decreaseBtn.disabled = false; // Always allow decrease for removal
            }
            
            if (increaseBtn) {
              const bundleState = this.bundleState || window.fsBundleState;
              const cardIsAddon = card.getAttribute('is') === 'fsb-productaddon' || card.dataset.category === 'add-ons';
              if (cardIsAddon) {
                increaseBtn.disabled = bundleState.addOnQuantity >= bundleState.addOnLimits.maximum;
              } else {
                increaseBtn.disabled = bundleState.currentQuantity >= bundleState.bundleQuantity;
              }
            }
          }
          
          // Show/hide quantity selector based on quantity
          const quantitySelector = card.querySelector('.fsb__quantity-selector');
          if (quantitySelector) {
            if (newQuantity === 0) {
              quantitySelector.classList.add('hidden');
            } else {
              quantitySelector.classList.remove('hidden');
            }
          }
        }
      }
    });
  }

  getAddonQuantityInBundle(variantId) {
    if (!variantId || !(this.bundleState || window.fsBundleState)) return 0;
    
    const bundleState = this.bundleState || window.fsBundleState;
    const key = variantId.toString();
    return bundleState.addOnItems[key]?.quantity || 0;
  }

  /**
   * Updates the visual state of the product card based on bundle capacity and selection
   */
  updateCardSelectionState() {
    if (!this.currentVariant) return;
    
    // Check if bundle is at maximum capacity for the selected bundle size
    const currentQuantity = this.bundleState.currentQuantity;
    const bundleQuantity = this.bundleState.bundleQuantity;
    const isAtCapacity = currentQuantity >= bundleQuantity;
    
    // Check if this specific product/variant is selected
    const variantId = this.currentVariant.id;
    const isProductSelected = this.isAddon 
      ? this.bundleState.addOnItems[variantId] && this.bundleState.addOnItems[variantId].quantity > 0
      : this.bundleState.bundleItems[variantId] && this.bundleState.bundleItems[variantId].quantity > 0;
    
    // For add-on items
    if (this.isAddon) {
      const addOnQuantity = this.bundleState.addOnQuantity;
      const maxAddOns = this.bundleState.addOnLimits.maximum;
      const isAddonAtCapacity = maxAddOns > 0 && addOnQuantity >= maxAddOns;
      
      // Gray out unselected add-on items ONLY when addon capacity is reached (not bundle capacity)
      if (isAddonAtCapacity && !isProductSelected) {
        this.classList.add('fsb__product-card--disabled');
      } else {
        this.classList.remove('fsb__product-card--disabled');
      }
    } else {
      // For regular bundle items, gray out unselected products when bundle reaches capacity
      if (isAtCapacity && !isProductSelected) {
        this.classList.add('fsb__product-card--disabled');
      } else {
        this.classList.remove('fsb__product-card--disabled');
      }
    }
  }
}

customElements.define('fsb-productcard', FsbProductCard);

/**
 * Bundle aside component
 * @customElement
 * @extends HTMLElement
 */
class FsbAside extends HTMLElement {
  /**
   * Constructor
   */
  constructor() {
    super();
    /**
     * @type {import('./types').BundleState}
     */
    this.bundleState = null;
    this.bundleOptions = null;
    this.continueButtons = null;
    this.addToCartButtons = null;
    this.backButtons = null;
    this.itemsContainer = null;
    this.addOnItemsSidebarContainer = null;
    this.addOnItemsContainers = null;
    this.bundlePriceElements = null;
    this.tabs = null;
    this.isExpanded = false;
    this.isMobile = window.innerWidth <= 749;
    this.bundleItemsWrapper = null;
  }

  /**
   * Called when element is connected to the DOM
   */
  connectedCallback() {
    BundleStateManager.initBundleState.call(this);
    this.bundleState.subscribe(this, this.handleStateChange);
    this.initializeElements();
    this.setupInitialState();
    this.addEventListeners();
    this.syncCookieTypeSelectors();
    this.setupMobileInteraction();
    // Ensure addon counts display immediately on page load
    this.ensureAddonCountsDisplay();

    // Handle resize events
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth <= 749;
      if (!this.isMobile) {
        this.classList.remove('expanded');
        this.isExpanded = false;
        if (this.bundleItemsWrapper) {
          this.bundleItemsWrapper.style.display = '';
        }
      }
    });
  }
  setupMobileInteraction() {
    if (!this.isMobile) return;

    const header = this.querySelector('.sidebar-header');
    this.bundleItemsWrapper = this.querySelector('.bundle-items-wrapper');

    if (!header || !this.bundleItemsWrapper) return;

    const toggleExpand = (e) => {
      if (!this.isMobile) return;

      // Don't toggle if clicking on interactive elements
      if (e.target.closest('button, a, select, input')) return;

      this.isExpanded = !this.isExpanded;
      this.classList.toggle('expanded', this.isExpanded);

      // Toggle only the bundle items wrapper
      if (this.bundleItemsWrapper) {
        this.bundleItemsWrapper.style.display = this.isExpanded ? 'block' : 'none';
      }
    };

    header.addEventListener('click', toggleExpand);

    // Close when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.isExpanded || !this.isMobile) return;
      if (!this.contains(e.target)) {
        this.isExpanded = false;
        this.classList.remove('expanded');
        if (this.bundleItemsWrapper) {
          this.bundleItemsWrapper.style.display = 'none';
        }
      }
    });
  }

  disconnectedCallback() {
    this.bundleState.unsubscribe(this);
    this.removeEventListeners();
  }

  /**
   * Handle bundle option change events
   * @param {CustomEvent} event - The bundle option change event
   */
  bundleOptionChangeHandler(event) {
    // Check if the change was automatic (e.g. from auto-sizing)
    if (this.bundleState && this.bundleState.currentView === 'addons') {
      // Check if the user needs to add more products to meet the new bundle size
      if (this.bundleState.currentQuantity < this.bundleState.bundleQuantity) {
        this.backToProducts();
      }
    }
    
    // Update the total price when bundle options change (e.g., box quantity changes)
    this.updateTotalPrice();
  }

  initializeElements() {
    this.bundleOptions = this.querySelector('fsb-bundleoptions');
    this.continueButtons = this.querySelectorAll('.fsb__bundle-continue');
    this.addToCartButtons = this.querySelectorAll('.fsb__bundle-add-to-cart');
    this.backButtons = this.querySelectorAll('.fsb__bundle-back');
    this.itemsContainer = this.querySelector('.bundle-items-container');
    this.addOnItemsSidebarContainer = this.querySelector('.addon-items-sidebar-container');
    this.addOnItemsContainers = this.querySelectorAll('.addon-items-container');
    this.bundlePriceElements = this.querySelectorAll('.bundle-price');
    this.tabs = this.closest('.fsb__tabs');
    this.bundleState.currentView = 'products';
    this.productsCountElements = document.querySelectorAll('.bundle-selection-count.products');
    this.addonsCountElements = document.querySelectorAll('.bundle-selection-count.addons');
    this.addonDotsContainers = document.querySelectorAll('.addon-dots-container');
    this.randomizeButtons = this.querySelectorAll('.fsb__button-randomize');
    setTimeout(() => {
      this.randomizeButtons.forEach((btn) => {
        btn?.classList.toggle('hidden', !this.bundleState.checkAllProductsHaveSingleVariant());
      });
    });
  }

  /**
   * Apply a function to each addon container
   * @param {Function} callback - Function to apply to each container
   */
  forEachAddonContainer(callback) {
    if (!this.addOnItemsContainers || this.addOnItemsContainers.length === 0) return;
    this.addOnItemsContainers.forEach((container) => callback(container));
  }

  setupInitialState() {
    this.hasAddons = this.addOnItemsContainers.length > 0 && this.bundleState.addOnLimits.maximum > 0;
    if (this.addOnItemsSidebarContainer) {
      this.addOnItemsSidebarContainer.classList.toggle('hidden', !this.hasAddons);
    }
    this.initializeAddonDots();
    this.updateUI();
  }

  handleStateChange(changeType) {
    switch (changeType) {
      case 'bundleItems':
        this.updateItemsDisplay();
        this.updateButtonStates();
        this.updateTotalPrice();
        this.updateSelectionCounts();
        this.updateAccordionStates();
        this.updateAddonDots();
        this.enforceBoxLimits();
        this.updateSidebarQuantitySelectors();

        // Always keep both bundle and addon sections expanded
        const bundleItemsContainer = this.querySelector('.bundle-items-container');
        const bundleDetailsWrapper = this.querySelector('.fsb__bundle-details-wrapper');
        if (bundleItemsContainer && bundleDetailsWrapper && !bundleItemsContainer.classList.contains('expanded')) {
          this.toggleAccordion(bundleDetailsWrapper, bundleItemsContainer);
        }

        // Keep addon section expanded too
        const addonItemsContainer = this.querySelector('.addon-items-container.fsb__desktop-only');
        const addonHeaderContainer = this.querySelector('.addon-items-header');
        if (addonItemsContainer && addonHeaderContainer && !addonItemsContainer.classList.contains('expanded')) {
          this.toggleAccordion(addonHeaderContainer, addonItemsContainer);
        }
        break;

      case 'addOnItems':
        this.updateItemsDisplay();
        this.updateButtonStates();
        this.updateTotalPrice();
        this.updateSelectionCounts();
        this.updateAddonDots();
        this.updateAddonsVisibility();
        this.updateSidebarQuantitySelectors();

        // Always keep both addon and bundle sections expanded
        const addonContainer = this.querySelector('.addon-items-container.fsb__desktop-only');
        const addonHeader = this.querySelector('.addon-items-header');
        if (addonContainer && addonHeader && !addonContainer.classList.contains('expanded')) {
          this.toggleAccordion(addonHeader, addonContainer);
        }

        // Keep bundle section expanded too
        const bundleContainer = this.querySelector('.bundle-items-container');
        const bundleHeader = this.querySelector('.fsb__bundle-details-wrapper');
        if (bundleContainer && bundleHeader && !bundleContainer.classList.contains('expanded')) {
          this.toggleAccordion(bundleHeader, bundleContainer);
        }
        break;

      case 'priceStrategy':
        this.updatePriceVisibility();
        this.updateTotalPrice();
        break;

      case 'bundleQuantity':
        this.initializeAddonDots();
        this.enforceBoxLimits();
        this.updateUI();
        break;

      case 'stateInitialized':
        this.removeExcessAddOns();
        this.removeExcessItems();
        this.initializeAddonDots();
        this.updateUI();
        this.initializeAccordionStates();
        this.bundleOptionChangeHandler();
        break;

      case 'view_changed':
        this.updateViewBasedUI();
        this.updateAccordionsBasedOnView();
        break;
    }
  }

  updateUI() {
    this.updateItemsDisplay();
    this.updateButtonStates();
    this.updatePriceVisibility();
    this.updateTotalPrice();
    this.updateSelectionCounts();
    this.updateViewBasedUI();
    this.updateSidebarQuantitySelectors();
  }

  /**
   * Ensure addon counts are displayed immediately
   */
  ensureAddonCountsDisplay() {
    const updateCounts = () => {
      if (this.addonsCountElements && this.bundleState) {
        this.updateSelectionCounts();
      }
    };

    // Try immediately
    updateCounts();
    
    // Also try after a short delay to ensure everything is ready
    setTimeout(updateCounts, 10);
    
    // And ensure it happens when the document is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', updateCounts);
    } else {
      updateCounts();
    }
  }

  updateViewBasedUI() {
    const currentView = this.bundleState.currentView;

    if (currentView === 'products') {
      // Update selection count visibility
      document.querySelectorAll('.bundle-selection-count.products').forEach((el) => {
        el.style.display = 'block';
      });
      document.querySelectorAll('.bundle-selection-count.addons').forEach((el) => {
        el.style.display = 'none';
      });
    } else if (currentView === 'addons') {
      document.querySelectorAll('.bundle-selection-count.products').forEach((el) => {
        el.style.display = 'none';
      });
      document.querySelectorAll('.bundle-selection-count.addons').forEach((el) => {
        el.style.display = 'block';
      });
    }
    
    // Always update addon dots when view changes
    this.updateAddonDots();
  }

  /**
   * Sets accordion state directly without animation
   * Used for initialization and programmatic state changes
   * @param {HTMLElement} container - The accordion content container
   * @param {boolean} expanded - Whether the accordion should be expanded
   */
  setAccordionState(container, expanded = true) {
    if (!container) return;
    
    // Don't set heights for containers inside unified container
    const isInUnified = container.closest('.unified-items-container');
    
    // Set state directly without animation
    if (!isInUnified) {
      container.style.height = expanded ? 'auto' : '0px';
    }
    container.classList.toggle('expanded', expanded);

    // Find and update the matching header's icon if possible
    const header = this.getAccordionHeaderForContent(container);
    if (header) {
      const icon = header.querySelector('.icon-caret');
      if (icon) {
        icon.classList.toggle('icon-caret--rotated', expanded);
      }
    }
  }

  /**
   * Helper to find the header element for a given accordion content
   * @param {HTMLElement} contentContainer - The accordion content element
   * @returns {HTMLElement|null} - The header element or null
   */
  getAccordionHeaderForContent(contentContainer) {
    if (!contentContainer) return null;

    // Determine which header this content belongs to
    if (contentContainer.classList.contains('bundle-items-container')) {
      return this.querySelector('.fsb__bundle-details-wrapper');
    } else if (contentContainer.classList.contains('addon-items-container')) {
      return this.querySelector('.addon-items-header');
    }
    return null;
  }

  /**
   * Helper to set caret (arrow) visibility and interactivity
   * @param {HTMLElement} icon
   * @param {boolean} visible
   */
  setCaretState(icon, visible = true) {
    if (!icon) return;
    icon.classList.toggle('hidden', !visible);
    icon.setAttribute('aria-hidden', String(!visible));
    icon.style.pointerEvents = visible ? '' : 'none';
    icon.tabIndex = visible ? 0 : -1;
  }

  /**
   * Initializes accordion functionality for bundle containers
   */
  initializeAccordions() {
    // Only enable accordion logic if both products and add-ons are available
    if (!this.hasAddons) {
      // Always expand bundle items and disable caret
      this.setAccordionState(this.querySelector('.bundle-items-container'), true);
      this.setCaretState(this.querySelector('.fsb__bundle-details-wrapper .icon-caret'), false);
      return;
    }
    // Setup accordion for bundle items wrapper
    const bundleItemsWrapper = this.querySelector('.fsb__bundle-details-wrapper');
    if (bundleItemsWrapper) {
      const bundleItemsContainer = this.querySelector('.bundle-items-container');
      if (bundleItemsContainer) {
        bundleItemsWrapper.addEventListener('click', (e) => {
          if (!this.hasAddons) return; // Guard
          if (e.target.closest('button:not(.accordion-toggle), a, select, input')) return;
          this.toggleAccordion(bundleItemsWrapper, bundleItemsContainer);
        });
      }
    }
    // Setup accordion for add-on items container
    const addonHeaderContainer = this.querySelector('.addon-items-header');
    if (addonHeaderContainer) {
      const addonItemsContainer = this.querySelector('.addon-items-container.fsb__desktop-only');
      if (addonItemsContainer) {
        addonHeaderContainer.addEventListener('click', (e) => {
          if (!this.hasAddons) return; // Guard
          if (e.target.closest('button:not(.accordion-toggle), a, select, input')) return;
          this.toggleAccordion(addonHeaderContainer, addonItemsContainer);
        });
      }
    }
    this.updateAddonsVisibility();
  }

  /**
   * Toggles the accordion with animation effects
   * Used for user interactions
   * @param {HTMLElement} header - The accordion header element
   * @param {HTMLElement} content - The accordion content container
   */
  toggleAccordion(header, content) {
    if (!this.hasAddons && !this.isMobile) return;

    // Don't animate height for containers inside unified container
    const isInUnified = content.closest('.unified-items-container');
    
    // Toggle expanded class
    const isExpanded = !content.classList.contains('expanded');
    content.classList.toggle('expanded', isExpanded);

    // Update icon rotation
    const icon = header.querySelector('.icon-caret');
    if (icon) {
      icon.classList.toggle('icon-caret--rotated', isExpanded);
    }

    // Skip height animations for unified container children
    if (isInUnified) return;

    // Apply height transition
    if (isExpanded) {
      // Expanding animation
      const contentHeight = content.scrollHeight;
      content.style.height = '0px';
      content.offsetHeight; // Force reflow
      content.style.height = `${contentHeight}px`;

      // Reset to auto after animation completes
      content.addEventListener(
        'transitionend',
        () => {
          content.style.height = 'auto';
        },
        { once: true }
      );
    } else {
      // Collapsing animation
      const currentHeight = content.scrollHeight;
      content.style.height = `${currentHeight}px`;
      content.offsetHeight; // Force reflow
      content.style.height = '0px';

      // Add proper cleanup after collapse animation completes
      content.addEventListener(
        'transitionend',
        () => {
          // Keep height at 0px but remove inline style if no longer needed
          if (!content.classList.contains('expanded')) {
            content.style.height = '0px';
          }
        },
        { once: true }
      );
    }
  }

  /**
   * Updates the visibility of add-on related elements based on add-on availability
   */
  updateAddonsVisibility() {
    // Use helper for caret and container
    const hasAddons = this.hasAddons;
    this.setCaretState(this.querySelector('.fsb__bundle-details-wrapper .icon-caret'), hasAddons);
    if (this.addOnItemsSidebarContainer) {
      this.addOnItemsSidebarContainer.classList.toggle('hidden', !hasAddons);
    }
  }
  updateSelectionCounts() {
    // Update products count
    this.productsCountElements.forEach((element) => {
      if (element) {
        const selectedProducts = this.bundleState.currentQuantity;
        const maxProducts = this.bundleState.bundleQuantity || 0;
        element.textContent = `${selectedProducts}/${maxProducts} ${window.fsbSettings.bundle_selection_count_text}`;
      }
    });

    // Update add-ons count - show total available addons
    this.addonsCountElements.forEach((element) => {
      if (element) {
        const totalAddonsAvailable = this.bundleState.addOnLimits.maximum || 0;
        const countSpan = element.querySelector('.count');
        if (countSpan) {
          countSpan.textContent = totalAddonsAvailable;
        }
        // Show the addon count when there are addons available (ignore hasAddons check)
        if (totalAddonsAvailable > 0) {
          element.style.display = 'block';
        } else {
          element.style.display = 'none';
        }
      }
    });
  }

  /**
   * Initialize the addon dots system
   * Creates visual dots representing available addon slots
   */
  initializeAddonDots() {
    if (!this.addonDotsContainers || this.addonDotsContainers.length === 0) {
      return;
    }
    
    const maxAddons = this.bundleState.addOnLimits.maximum;
    console.log('Initializing addon dots - max addons:', maxAddons, 'containers:', this.addonDotsContainers.length);
    
    if (maxAddons <= 0) {
      this.addonDotsContainers.forEach((container) => {
        container.style.display = 'none';
      });
      return;
    }

    this.addonDotsContainers.forEach((container) => {
      const wrapper = container.querySelector('.addon-dots-wrapper');
      if (!wrapper) {
        return;
      }

      // Clear existing dots
      wrapper.innerHTML = '';

      // Generate dots based on maximum addon limit (no label)
      for (let i = 0; i < maxAddons; i++) {
        const dot = document.createElement('div');
        dot.className = 'addon-dot';
        dot.setAttribute('data-dot-index', i);
        // Remove any click event listeners - dots are display only
        dot.style.pointerEvents = 'none';
        wrapper.appendChild(dot);
      }

      // Show container
      container.style.display = 'block';
      console.log('Addon dots initialized with', maxAddons, 'dots');
    });
  }

  /**
   * Update the addon dots to reflect current selection
   * Fills dots based on selected addon quantity
   */
  updateAddonDots() {
    if (!this.addonDotsContainers || this.addonDotsContainers.length === 0) return;

    const selectedAddons = this.bundleState.addOnQuantity;
    const maxAddons = this.bundleState.addOnLimits.maximum;

    this.addonDotsContainers.forEach((container) => {
      const dots = container.querySelectorAll('.addon-dot');
      
      dots.forEach((dot, index) => {
        if (index < selectedAddons) {
          dot.classList.add('filled');
        } else {
          dot.classList.remove('filled');
        }
      });

      // Show container if addons are available
      const shouldShow = maxAddons > 0;
      container.style.display = shouldShow ? 'block' : 'none';
    });
  }

  /**
   * Enforce bundle quantity limits to prevent selection beyond box capacity
   * Disables add buttons when box is full and enables addons to take over
   */
  enforceBoxLimits() {
    const maxBundleQuantity = this.bundleState.bundleQuantity;
    const currentBundleQuantity = this.bundleState.currentQuantity;
    
    console.log('enforceBoxLimits called - current:', currentBundleQuantity, 'max:', maxBundleQuantity);
    
    // If bundle is at max capacity, disable adding more bundle items (but NOT addon items)
    if (currentBundleQuantity >= maxBundleQuantity) {
      console.log('Bundle is at max capacity - disabling buttons and calling autoAdvanceToAddons');
      
      // Find all product cards and disable add buttons if they're not already selected
      // BUT exclude addon cards (those in the addons tab)
      const productCards = document.querySelectorAll('fsb-productcard');
      productCards.forEach((card) => {
        // Skip addon cards - check if the card is in the addons tab
        const isAddonCard = card.closest('#addons') !== null || 
                           card.closest('.fsb__addons-container') !== null ||
                           card.getAttribute('data-category') === 'add-ons';
        
        if (isAddonCard) {
          return; // Don't disable addon cards
        }
        
        const variantId = card.getAttribute('data-variant-id');
        const isSelected = Object.keys(this.bundleState.bundleItems).some(key => 
          key.startsWith(variantId + '_')
        );
        
        if (!isSelected) {
          const addButton = card.querySelector('.fsb__button-add-to-bundle, .product-card__add-button');
          const quickAddButton = card.querySelector('.fsb__quantity-selector-increase');
          
          if (addButton) {
            addButton.disabled = true;
            addButton.style.opacity = '0.5';
            addButton.style.pointerEvents = 'none';
            addButton.style.cursor = 'not-allowed';
            addButton.setAttribute('data-box-limit-reached', 'true');
          }
          
          if (quickAddButton) {
            quickAddButton.disabled = true;
            quickAddButton.style.opacity = '0.5';
            quickAddButton.style.pointerEvents = 'none';
            quickAddButton.style.cursor = 'not-allowed';
            quickAddButton.setAttribute('data-box-limit-reached', 'true');
          }
        }
      });
      
      // Auto-advance to addons tab if addons are available
      this.autoAdvanceToAddons();
      
    } else {
      // Re-enable add buttons if under capacity (excluding addon cards which should always be enabled)
      const productCards = document.querySelectorAll('fsb-productcard');
      productCards.forEach((card) => {
        // Skip addon cards - they should always be enabled
        const isAddonCard = card.closest('#addons') !== null || 
                           card.closest('.fsb__addons-container') !== null ||
                           card.getAttribute('data-category') === 'add-ons';
        
        if (isAddonCard) {
          return; // Don't modify addon cards
        }
        
        const addButton = card.querySelector('.fsb__button-add-to-bundle, .product-card__add-button');
        const quickAddButton = card.querySelector('.fsb__quantity-selector-increase');
        
        if (addButton && addButton.hasAttribute('data-box-limit-reached')) {
          addButton.disabled = false;
          addButton.style.opacity = '';
          addButton.style.pointerEvents = '';
          addButton.style.cursor = '';
          addButton.removeAttribute('data-box-limit-reached');
        }
        
        if (quickAddButton && quickAddButton.hasAttribute('data-box-limit-reached')) {
          quickAddButton.disabled = false;
          quickAddButton.style.opacity = '';
          quickAddButton.style.pointerEvents = '';
          quickAddButton.style.cursor = '';
          quickAddButton.removeAttribute('data-box-limit-reached');
        }
      });
      
      // Hide the addons tab if box is no longer full and switch back to a product tab
      this.hideAddonsTabIfNotFull();
    }
  }

  /**
   * Auto-advance to addons tab when box is full - DISABLED for manual continue button workflow
   */
  autoAdvanceToAddons() {
    // Only show visual feedback when bundle is full, but don't auto-advance
    const hasAddons = this.bundleState.addOnLimits.maximum > 0;
    
    if (!hasAddons) {
      return;
    }
    
    console.log('Bundle is full - continue button should now be available');
    // The continue button will handle the transition to addons
    // No auto-advance - user clicks continue button manually
  }

  /**
   * Hide addons tab and switch back to product tab when box is no longer full
   */
  hideAddonsTabIfNotFull() {
    // Use the existing backToProducts method to properly switch back
    if (this.isInAddOnsSection()) {
      console.log('Bundle no longer full - switching back to products view');
      this.backToProducts();
    }
  }

  addEventListeners() {
    // Bind methods once
    this.continueToAddOns = this.continueToAddOns.bind(this);
    this.backToProducts = this.backToProducts.bind(this);
    this.handleRandomize = this.handleRandomize.bind(this);

    // Add listeners to all instances of each button
    this.addToCartButtons.forEach((button) => {
      button.addEventListener('click', () => this.addBundleToCart());
    });

    this.continueButtons.forEach((button) => {
      button.addEventListener('click', this.continueToAddOns);
    });

    this.backButtons.forEach((button) => {
      button.addEventListener('click', this.backToProducts);
    });

    this.randomizeButtons.forEach((button) => {
      button.addEventListener('click', this.handleRandomize);
    });

    // Listen for bundle option change events from fsb-bundleoptions
    if (this.bundleOptions) {
      this.bundleOptions.addEventListener('bundleOptionChange', this.bundleOptionChangeHandler.bind(this));
    }

    this.syncCookieTypeSelectors();
    this.initializeAccordions();
  }

  syncCookieTypeSelectors() {
    this.addEventListener('change', (event) => {
      const radio = event.target;
      if (!radio?.matches?.('.fsb__cookie-type-selector input[type="radio"]')) return;
      if (!radio.checked) return;

      this.querySelectorAll('.fsb__cookie-type-selector input[type="radio"]').forEach((other) => {
        other.checked = other.value === radio.value;
      });
    });
  }

  updateAddonsContainerInfo() {
    const addonsSidebarContainer = this.addOnItemsSidebarContainer;
    if (addonsSidebarContainer) {
      addonsSidebarContainer.classList.toggle('hidden', !this.hasAddons);
    }
  }

  updateItemsDisplay() {
    this.updateBundleItems();
    this.updateAddonItems();
  }

  updateAddonItems() {
    this.forEachAddonContainer((container) => {
      // Get existing items and create a map for quick lookup
      const existingItems = container.querySelectorAll('.bundle-item:not([data-bundle-item-template])');
      const existingItemsMap = new Map();
      existingItems.forEach(item => {
        const itemKey = item.getAttribute('data-item-key');
        if (itemKey) {
          existingItemsMap.set(itemKey, item);
        }
      });

      // Track which items should exist
      const currentItemKeys = new Set(Object.keys(this.bundleState.addOnItems));

      // Remove items that no longer exist in state
      existingItemsMap.forEach((element, key) => {
        if (!currentItemKeys.has(key)) {
          element.remove();
          existingItemsMap.delete(key);
        }
      });

      // Add new items (only items that don't already exist in DOM)
      Object.entries(this.bundleState.addOnItems).forEach(([itemKey, itemData]) => {
        if (!existingItemsMap.has(itemKey)) {
          // This is a new item, add it
          const variantData = {
            id: itemData.variantId,
            title: itemData.variantTitle,
            media_link: itemData.image,
            bundlePrice: itemData.price || 0
          };
          const productData = {
            title: itemData.title,
            featured_image: itemData.image,
            metafields: itemData.metafields,
            containsNuts: itemData.containsNuts
          };
          this.addItemToDOM(itemKey, variantData, productData, container);
        } else {
          // This item already exists, just update its quantity if needed
          const existingElement = existingItemsMap.get(itemKey);
          const quantityInput = existingElement.querySelector('.fsb__quantity-selector-input');
          if (quantityInput && parseInt(quantityInput.value, 10) !== itemData.quantity) {
            quantityInput.value = itemData.quantity;
          }
        }
      });

      // Show/hide placeholder
      const placeholder = container.querySelector('.items-placeholder');
      if (placeholder) {
        if (Object.keys(this.bundleState.addOnItems).length === 0) {
          placeholder.removeAttribute('hidden');
        } else {
          placeholder.setAttribute('hidden', 'true');
        }
      }
    });
  }

  updateBundleItems() {
    const currentItems = this.itemsContainer.querySelectorAll('.bundle-item:not([data-bundle-item-template])');
    const currentItemsMap = new Map();

    // Create a map of existing DOM elements by item key
    currentItems.forEach((item) => {
      const itemKey = item.getAttribute('data-item-key');
      if (itemKey) currentItemsMap.set(itemKey, item);
    });

    // Track keys that should exist after update
    const updatedItemKeys = new Set();

    // Update existing items or add new ones
    Object.entries(this.bundleState.bundleItems).forEach(([itemKey, itemData]) => {
      updatedItemKeys.add(itemKey);
      const { variantId, title, variantTitle, image, price, quantity, metafields, containsNuts } = itemData;
      const variantData = { id: variantId, title: variantTitle, media_link: image, bundlePrice: price };
      const productData = { title, featured_image: image, metafields: metafields, containsNuts: containsNuts };

      if (currentItemsMap.has(itemKey)) {
        // Update existing item
        this.updateItemInDOM(currentItemsMap.get(itemKey), itemData);
      } else {
        // Add new item
        this.addItemToDOM(itemKey, variantData, productData, this.itemsContainer);
      }
    });

    // Remove items that no longer exist in the state
    currentItemsMap.forEach((element, key) => {
      if (!updatedItemKeys.has(key)) {
        element.remove();
      }
    });

    // Add placeholder if bundle is empty
    if (Object.keys(this.bundleState.bundleItems).length === 0) {
      const existingPlaceholder = this.itemsContainer.querySelector('.items-placeholder');
      if (existingPlaceholder) existingPlaceholder.removeAttribute('hidden');
    } else {
      // Hide any existing placeholder if items exist
      const existingPlaceholder = this.itemsContainer.querySelector('.items-placeholder');
      if (existingPlaceholder) existingPlaceholder.setAttribute('hidden', true);
    }
  }

  updateItemInDOM(itemElement, itemData) {
    if (!itemElement) return;

    // Update quantity
    const qtyElement = itemElement.querySelector('.bundle-item__qty');
    if (qtyElement) {
      qtyElement.textContent = itemData.quantity;
    }

    // Update price if needed
    const priceElement = itemElement.querySelector('.bundle-item__price');
    if (priceElement) {
      priceElement.innerHTML =
        itemData.price === 0 ? window.fsbSettings.free_label || 'Free' : Fsb.formatMoney(itemData.price);
    }

    // Only update other elements if they've changed
    // This avoids unnecessary DOM operations
    const nameElement = itemElement.querySelector('.bundle-item__name');
    const displayTitle = itemData.title.split(' - ')[0];
    if (nameElement && nameElement.textContent !== displayTitle) {
      nameElement.textContent = displayTitle;
    }

    const variantTitleElement = itemElement.querySelector('.bundle-item__variant-title');
    if (variantTitleElement && itemData.variantTitle && variantTitleElement.textContent !== itemData.variantTitle) {
      variantTitleElement.textContent = itemData.variantTitle;
    }

    const imageElement = itemElement.querySelector('.bundle-item__image');
    if (imageElement && imageElement.src !== itemData.image) {
      imageElement.src = itemData.image || imageElement.src;
    }

    // Update quantity selector input if present
    const quantityInput = itemElement.querySelector('.fsb__quantity-selector-input');
    if (quantityInput && parseInt(quantityInput.value, 10) !== itemData.quantity) {
      quantityInput.value = itemData.quantity;
    }

    // Update quantity selector button states
    const decreaseBtn = itemElement.querySelector('.fsb__quantity-selector-decrease');
    const increaseBtn = itemElement.querySelector('.fsb__quantity-selector-increase');
    
    if (decreaseBtn) {
      decreaseBtn.disabled = itemData.quantity <= 0;
    }
    
    if (increaseBtn) {
      increaseBtn.disabled = this.bundleState.currentQuantity >= this.bundleState.bundleQuantity;
    }
  }

  /**
   * Updates the quantity of a bundle item
   * @param {string} itemKey - The item key
   * @param {number} newQuantity - The new quantity
   */
  updateBundleItemQuantity(itemKey, newQuantity) {
    if (!this.bundleState.bundleItems[itemKey]) return;
    
    const currentQuantity = this.bundleState.bundleItems[itemKey].quantity;
    const quantityDiff = newQuantity - currentQuantity;
    
    // Update the bundle state
    this.bundleState.bundleItems[itemKey].quantity = newQuantity;
    this.bundleState.currentQuantity += quantityDiff;
    
    // If quantity is 0, remove the item
    if (newQuantity === 0) {
      delete this.bundleState.bundleItems[itemKey];
    }
    
    // Notify subscribers of the change
    this.bundleState.notifySubscribers('bundleItems');
  }

  /**
   * Syncs quantity from sidebar to product cards
   * @param {string} itemKey - The item key (variant ID)
   * @param {number} newQuantity - The new quantity
   */
  syncQuantityWithProductCards(itemKey, newQuantity) {
    // Find product cards with matching variant ID
    const productCards = document.querySelectorAll(`fsb-productcard[data-variant-id="${itemKey}"], fsb-productaddon[data-variant-id="${itemKey}"]`);
    
    productCards.forEach(card => {
      const quantityInput = card.querySelector('.fsb__quantity-selector-input');
      if (quantityInput && parseInt(quantityInput.value, 10) !== newQuantity) {
        quantityInput.value = newQuantity;
        
        // Update button states on the product card
        const container = quantityInput.closest('.fsb__quantity-selector');
        if (container) {
          const decreaseBtn = container.querySelector('.fsb__quantity-selector-decrease');
          const increaseBtn = container.querySelector('.fsb__quantity-selector-increase');
          
          if (decreaseBtn) {
            decreaseBtn.disabled = newQuantity <= 0;
          }
          
          if (increaseBtn) {
            const cardIsAddon = card.getAttribute('is') === 'fsb-productaddon' || card.dataset.category === 'add-ons';
            if (cardIsAddon) {
              increaseBtn.disabled = this.bundleState.addOnQuantity >= this.bundleState.addOnLimits.maximum;
            } else {
              increaseBtn.disabled = this.bundleState.currentQuantity >= this.bundleState.bundleQuantity;
            }
          }
        }
        
        // Show/hide quantity selector based on quantity
        const quantitySelector = card.querySelector('.fsb__quantity-selector');
        if (quantitySelector) {
          if (newQuantity === 0) {
            quantitySelector.classList.add('hidden');
          } else {
            quantitySelector.classList.remove('hidden');
          }
        }
      }
    });
  }

  /**
   * Syncs addon quantity from sidebar to product cards
   * @param {string} variantId - The variant ID
   * @param {number} newQuantity - The new quantity
   */
  syncAddonQuantityWithProductCards(variantId, newQuantity) {
    // Find product cards with matching variant ID
    const productCards = document.querySelectorAll(`fsb-productcard[data-variant-id="${variantId}"], fsb-productaddon[data-variant-id="${variantId}"]`);
    
    productCards.forEach(card => {
      const quantityInput = card.querySelector('.fsb__quantity-selector-input');
      if (quantityInput && parseInt(quantityInput.value, 10) !== newQuantity) {
        quantityInput.value = newQuantity;
        
        // Update button states on the product card
        const container = quantityInput.closest('.fsb__quantity-selector');
        if (container) {
          const decreaseBtn = container.querySelector('.fsb__quantity-selector-decrease');
          const increaseBtn = container.querySelector('.fsb__quantity-selector-increase');
          
          if (decreaseBtn) {
            decreaseBtn.disabled = newQuantity <= 0;
          }
          
          if (increaseBtn) {
            increaseBtn.disabled = this.bundleState.addOnQuantity >= this.bundleState.addOnLimits.maximum;
          }
        }
        
        // Show/hide quantity selector based on quantity
        const quantitySelector = card.querySelector('.fsb__quantity-selector');
        if (quantitySelector) {
          if (newQuantity === 0) {
            quantitySelector.classList.add('hidden');
          } else {
            quantitySelector.classList.remove('hidden');
          }
        }
      }
    });
  }



  updateButtonStates() {
    this.updateAddToCartButtonState();
    this.updateContinueButtonState();
    this.updateBackButtonState();
  }

  shouldDisableAddToCart() {
    if (
      this.hasAddons &&
      this.bundleState.addOnLimits.maximum > 0 &&
      this.bundleState.addOnQuantity < this.bundleState.addOnLimits.minimum
    )
      return true;
    return !!(this.bundleState.currentQuantity !== this.bundleState.bundleQuantity);
  }

  shouldDisableContinue() {
    return this.bundleState.currentQuantity !== this.bundleState.bundleQuantity;
  }

  updateAddToCartButtonState() {
    if (this.addToCartButtons.length === 0) return;
    const isDisabled = this.shouldDisableAddToCart();
    const shouldHide = this.hasAddons && !this.isInAddOnsSection();

    this.addToCartButtons.forEach((button) => {
      button.disabled = isDisabled;
      button.classList.toggle('hidden', shouldHide);
    });
  }

  updateContinueButtonState() {
    if (this.continueButtons.length === 0) return;
    const isDisabled = this.shouldDisableContinue();
    const shouldHide = !this.hasAddons || this.isInAddOnsSection();

    this.continueButtons.forEach((button) => {
      button.disabled = isDisabled;
      button.classList.toggle('hidden', shouldHide);
    });

    const shouldHideRandomize = this.isInAddOnsSection() || !this.bundleState.checkAllProductsHaveSingleVariant();

    this.randomizeButtons.forEach((button) => {
      button.classList.toggle('hidden', shouldHideRandomize);
    });
  }

  updateBackButtonState() {
    if (this.backButtons.length === 0) return;
    const shouldHide = !this.isInAddOnsSection();

    this.backButtons.forEach((button) => {
      button.classList.toggle('hidden', shouldHide);
    });
  }

  isInAddOnsSection() {
    const addonContainer = document.querySelector('.fsb__addons-container');
    return addonContainer && !addonContainer.classList.contains('hidden');
  }

  continueToAddOns() {
    if (!this.hasAddons) return;
    
    // Hide all individual tabs and show the addon container (within tabs-container)
    const allTabs = document.querySelectorAll('fsb-tab');
    const addonContainer = document.querySelector('.fsb__addons-container');
    
    allTabs.forEach(tab => {
      tab.classList.add('hidden');
    });
    
    if (addonContainer) {
      addonContainer.classList.remove('hidden');
    }
    
    // Disable dropdown selector during addon stage
    this.setDropdownState(false);
    
    // Show back button and hide continue button
    this.setBackButtonVisibility(true);
    this.setContinueButtonVisibility(false);
    
    this.updateButtonStates();
    this.bundleState.currentView = 'addons';
    this.bundleState.notifySubscribers('view_changed');
  }

  backToProducts() {
    // Show all individual tabs and hide the addon container
    const allTabs = document.querySelectorAll('fsb-tab');
    const addonContainer = document.querySelector('.fsb__addons-container');
    
    allTabs.forEach((tab, index) => {
      tab.classList.remove('hidden');
      // Restore active state to first tab
      if (index === 0) {
        tab.classList.add('active');
      } else {
        tab.classList.remove('active');
      }
    });
    
    if (addonContainer) {
      addonContainer.classList.add('hidden');
    }
    
    // Re-enable dropdown selector when back to bundle stage
    this.setDropdownState(true);
    
    // Hide back button and show continue button
    this.setBackButtonVisibility(false);
    this.setContinueButtonVisibility(true);
    
    this.updateButtonStates();
    this.bundleState.currentView = 'products';
    this.bundleState.notifySubscribers('view_changed');
  }

  setDropdownState(enabled) {
    const bundleOptions = this.querySelector('fsb-bundleoptions');
    if (bundleOptions) {
      const popover = bundleOptions.querySelector('.fsb__popover');
      if (popover) {
        if (enabled) {
          popover.classList.remove('disabled');
          popover.style.pointerEvents = 'auto';
          popover.style.opacity = '1';
        } else {
          popover.classList.add('disabled');
          popover.style.pointerEvents = 'none';
          popover.style.opacity = '0.5';
        }
      }
    }

    // Toggle the pre/post addon text spans in bundle-selection-inline-count
    const inlineCount = this.querySelector('.bundle-selection-inline-count');
    if (inlineCount) {
      const preAddonText = inlineCount.querySelector('.pre-addon-text');
      const postAddonText = inlineCount.querySelector('.post-addon-text');
      
      if (preAddonText && postAddonText) {
        if (enabled) {
          // Show "Fill your box to unlock your" text (products view)
          preAddonText.style.display = 'inline';
          postAddonText.style.display = 'none';
        } else {
          // Show "You've unlocked your" text (addons view)
          preAddonText.style.display = 'none';
          postAddonText.style.display = 'inline';
        }
      }
    }
  }

  setBackButtonVisibility(visible) {
    this.backButtons.forEach(button => {
      if (visible) {
        button.classList.remove('hidden');
      } else {
        button.classList.add('hidden');
      }
    });
  }

  setContinueButtonVisibility(visible) {
    this.continueButtons.forEach(button => {
      if (visible) {
        button.classList.remove('hidden');
      } else {
        button.classList.add('hidden');
      }
    });
  }

  updatePriceVisibility(priceStrategy = this.bundleState.priceStrategy.strategy) {
    const tabsContainer = document.querySelector('.fsb__tabs-container');
    if (tabsContainer) {
      if (priceStrategy === 'fixed_pricing') {
        tabsContainer.classList.add('fsb__price-hidden');
      } else {
        tabsContainer.classList.remove('fsb__price-hidden');
      }
    }
  }

  parseBundleSettings() {
    const settingsString = this.getAttribute('data-bundle-settings');
    if (settingsString) {
      try {
        const settings = JSON.parse(settingsString);
        this.addOnLimits = settings.add_on || this.addOnLimits;
        this.priceStrategy = settings.price || this.priceStrategy;
      } catch (error) {
        console.error('Error parsing data-bundle-settings:', error);
      }
    }
  }

  initializeBundleQuantity(bundleOptions) {
    const selectedOption = bundleOptions.querySelector('[selected]');
    if (selectedOption) {
      this.bundleQuantity = parseInt(selectedOption.getAttribute('data-quantity')) || 0;
      this.selectedOption = selectedOption.dataset.optionId;
      const optionSettings = JSON.parse(selectedOption.dataset.option || '{}');
      if (optionSettings.add_on) {
        this.addOnLimits = optionSettings.add_on;
      }
      if (optionSettings.price) {
        this.priceStrategy = optionSettings.price;
      }
    } else {
      console.warn('No selected option found in fsb-bundleoptions.');
    }
  }

  addBundleToCart() {
    if (this.shouldDisableAddToCart()) {
      this.showErrorMessage(
        window.fsbSettings.addon_required_message || 'Please add the minimum required add-ons to continue.'
      );
      return;
    }

    this.selectedOption = this.bundleState.selectedOption || this.dataset.variant;
    const identifier = `${bundleId}_${Date.now()}`;
    this.hideError();

    const cookieType = this.getSelectedCookieType();
    const bundleLineItemProperties = this.prepareBundleLineItems(cookieType);
    const selectedItemsProperties = this.prepareSelectedItemsProperties();

    // Create cart form data
    const addToCartFormData = {
      items: [
        {
          id: parseInt(this.selectedOption, 10),
          quantity: 1,
          properties: {
            ...selectedItemsProperties,
            ...(cookieType ? { Preparation: cookieType } : {}),
            '__foxsell:dynamic_add_on_bundle_id': identifier,
            '__foxsell:dynamic_add_on_bundle_items': JSON.stringify(bundleLineItemProperties),
          },
        },
      ],
      sections: ['cart-drawer', 'cart-icon-bubble'],
    };

    // Add to cart with proper error handling
    this.submitCartRequest(addToCartFormData);
  }
  prepareBundleLineItems(cookieType) {
    const preparationProperty = cookieType ? { __Preparation: cookieType } : {};

    const bundleItems = Object.entries(this.bundleState.bundleItems).map(([variantId, itemData]) => ({
      variantId: parseInt(variantId, 10),
      quantity: itemData.quantity,
      type: 'product',
      properties: {
        ...preparationProperty,
      },
    }));

    const addonItems = Object.entries(this.bundleState.addOnItems).map(([variantId, itemData]) => ({
      variantId: parseInt(variantId, 10),
      quantity: itemData.quantity,
      type: 'addOns',
      properties: {
        [window.fsbSettings.personalization_property_name || 'Gift Message']: itemData.personalization || '',
        ...preparationProperty,
      },
    }));

    return [...bundleItems, ...addonItems];
  }

  getSelectedCookieType() {
    const selected = this.querySelector('.fsb__cookie-type-selector input[type="radio"]:checked');
    return selected?.dataset.propertyValue || selected?.value || '';
  }

  prepareSelectedItemsProperties() {
    const properties = {};
    const propNameKey = window.fsbSettings.personalization_property_name || 'Gift Message';

    // Only process personalization properties, skip summary properties that may show as line items
    const allItems = [
      ...Object.entries(this.bundleState.bundleItems).map(([id, data]) => ({ ...data, isAddon: false })),
      ...Object.entries(this.bundleState.addOnItems).map(([id, data]) => ({ ...data, isAddon: true })),
    ];

    // Process personalization properties only
    allItems.forEach((itemData) => {
      // Add personalization as separate property if present
      if (itemData.personalization) {
        const itemName = itemData.title.split(' - ')[0];
        const safeKey = `${itemName} - ${propNameKey}`.substring(0, 40);
        properties[safeKey] = itemData.personalization.substring(0, 100);
      }
    });

    return properties;
  }

  submitCartRequest(data) {
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }

        this.showSuccessMessage(window.fsbSettings.added_toast_message || 'Bundle successfully added to cart!');
        this.updateCartUI(response);
      })
      .catch((error) => {
        console.error('Error adding bundle to cart:', error);
        this.showErrorMessage(
          window.fsbSettings.cart_error_message || 'An error occurred while adding to cart. Please try again.'
        );
      });
  }

  // 5. Optimize error message handling
  showErrorMessage(message) {
    const errorMessageWrapper = document.querySelector('.fsb__error-message-wrapper');
    if (!errorMessageWrapper) return;

    const errorMessageElement = errorMessageWrapper.querySelector('.fsb__error-message');
    if (errorMessageElement) {
      errorMessageElement.textContent = message;
    }

    errorMessageWrapper.toggleAttribute('hidden', !message);
    console.error('Error:', message);
  }

  handleErrorMessage(errorMessage) {
    const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
    this.showErrorMessage(errorMessage);
    if (soldOutMessage) {
      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButtonText.classList.add('hidden');
      soldOutMessage.classList.remove('hidden');
    }
    this.error = true;
  }

  showSuccessMessage(message) {
    // Remove any existing toast
    let toast = document.querySelector('.fsb__toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'fsb__toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('fsb__toast--visible');

    // Remove after 2.5s
    clearTimeout(this._toastTimeout);
    this._toastTimeout = setTimeout(() => {
      toast.classList.remove('fsb__toast--visible');
    }, 2000);

    // Allow dismiss on click
    toast.onclick = () => {
      toast.classList.remove('fsb__toast--visible');
      clearTimeout(this._toastTimeout);
    };
  }

  hideError() {
    const errorMessageWrapper = document.querySelector('.fsb__error-message-wrapper');
    errorMessageWrapper.setAttribute('hidden', '');
  }

  showErrorMessage(message) {
    const errorMessageWrapper = document.querySelector('.fsb__error-message-wrapper');
    const errorMessageElement = errorMessageWrapper.querySelector('.fsb__error-message');
    errorMessageWrapper.toggleAttribute('hidden', !message);
    if (message) {
      errorMessageElement.textContent = message;
    }
    console.error('Error:', message);
  }

  updateCartUI(response) {
    if (fsbAdditionalSettings?.custom_cart_event) {
      const events = fsbAdditionalSettings.custom_cart_event.split(',').map((event) => event.trim());
      events.forEach((event) => {
        document.dispatchEvent(
          new CustomEvent(event, {
            bubbles: true,
          })
        );
      });
    }

    const cart = document.querySelector('cart-drawer');
    if (cart) {
      try {
        cart.renderContents(response);
        cart.open();
        if (cart && cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
      } catch (e) {
        if (fsbAdditionalSettings?.redirect_to_cart !== false) {
          window.location.href = fsbAdditionalSettings?.cart_url || window?.routes?.cart_url || '/cart';
        }
      }
    } else {
      if (fsbAdditionalSettings?.redirect_to_cart !== false) {
        window.location.href = fsbAdditionalSettings?.cart_url || window?.routes?.cart_url || '/cart';
      }
    }
  }

  isAllTabActive() {
    return this.tabs?.querySelector('.tabs__navigation-link.active')?.classList.contains('tabs__navigation-link--all');
  }

  getCurrentTabIndex() {
    const activeTab = this.tabs.querySelector('.tabs__navigation-link.active');
    return Array.from(this.tabs.querySelectorAll('.tabs__navigation-link')).indexOf(activeTab);
  }

  /**
   * Handles click on the randomize button
   * Clears the current bundle and fills it with random products
   */
  handleRandomize() {
    // Display a notification to the user
    Fsb.showToast(window.fsbSettings.randomize_notification);

    // Get all eligible products efficiently
    const availableProducts = this.getRandomizableProducts();

    if (!availableProducts.length) {
      return;
    }

    // Calculate how many more items we need to add
    const targetQuantity = this.bundleState.bundleQuantity;
    const currentQuantity = this.bundleState.currentQuantity;
    const itemsToAdd = Math.max(0, targetQuantity - currentQuantity);

    if (itemsToAdd === 0) {
      return;
    }

    // Randomize the bundle with safety limits
    const addedCount = this.randomizeBundleWithProducts(availableProducts, itemsToAdd);
  }

  /**
   * Gets all products that can be randomized
   * Filters for single-variant products that are available and visible
   * @returns {Array} Array of eligible products
   */
  getRandomizableProducts() {
    // Create a Set to avoid duplicate products
    const productSet = new Set();
    // Cache for variant availability status
    const availabilityCache = new Map();

    // Get active tabs (categories) once
    const activeTabs = document.querySelectorAll('.tabs__content-item.active');

    // Process each active tab
    activeTabs.forEach((tab) => {
      // Only process visible product cards - do this once per tab
      const productCards = tab.querySelectorAll('fsb-productcard:not([hidden]):not([is="fsb-productaddon"])');

      productCards.forEach((card) => {
        try {
          // Check for valid product data
          if (!card.dataset.product) return;

          const productData = JSON.parse(card.dataset.product);
          const variantSelector = card.querySelector('fsb-variantselector');

          // Skip invalid products
          if (!variantSelector || !productData.id) return;

          // Get variants and check if there's only one
          const variants = variantSelector.variants || [];
          if (variants.length !== 1) return;

          const variant = variants[0];
          const cacheKey = `${productData.id}-${variant.id}`;

          // Check cache for availability status
          let isAvailable = availabilityCache.get(cacheKey);

          if (isAvailable === undefined) {
            // Use the card's availability check method
            isAvailable = card.isVariantAvailable && card.isVariantAvailable(variant) === true;
            availabilityCache.set(cacheKey, isAvailable);
          }

          if (isAvailable) {
            // Use the variant ID as a key to avoid duplicates
            if (!productSet.has(variant.id)) {
              productSet.add(variant.id);
              // Store minimal required data
              const product = {
                card,
                product: productData,
                variant: variant,
              };
              // Precompute any necessary properties right away
              product.key = variant.id;
              
              product.bundleData = {
                price: variant.bundlePrice,
                title: productData.title.split(' - ')[0],
                variantTitle: !variant.is_default ? variant.title : null,
                image: variant.media_link || productData.featured_image,
                variantId: variant.id,
                productId: productData.id,
              };
              productSet.add(product);
            }
          }
        } catch (e) {
          // Silently handle errors without breaking the flow
        }
      });
    });

    // Convert set to array for further processing
    return Array.from(productSet).filter((item) => typeof item === 'object');
  }

  /**
   * Fills the bundle with randomly selected products
   * @param {Array} availableProducts Array of eligible products
   * @param {Number} itemsToAdd Number of items to add
   * @returns {Number} Number of items actually added
   */
  randomizeBundleWithProducts(availableProducts, itemsToAdd) {
    if (!availableProducts.length || itemsToAdd <= 0) {
      return 0;
    }

    // Shuffle the products efficiently
    const shuffledProducts = this.shuffleArray(availableProducts);

    // Keep track of how many products we've added
    let addedCount = 0;

    // Safety limits to prevent infinite loops
    const maxAttempts = Math.min(availableProducts.length * 3, 100);
    let attempts = 0;

    // Set of variant IDs we've already tried to add
    const attemptedVariants = new Set();

    // Add products until we reach the target or hit safety limits
    while (addedCount < itemsToAdd && attempts < maxAttempts) {
      attempts++;

      // If we've tried all products at least once, get a random one
      const index =
        attempts <= shuffledProducts.length ? attempts - 1 : Math.floor(Math.random() * shuffledProducts.length);

      const product = shuffledProducts[index];

      // Skip if we've already tried this variant
      if (attemptedVariants.has(product.key)) {
        continue;
      }

      // Mark this variant as attempted
      attemptedVariants.add(product.key);

      const success = this.bundleState.addBundleItem(product.key, product.bundleData);

      if (success) {
        addedCount++;

        // If we've added enough items, stop
        if (addedCount >= itemsToAdd) {
          break;
        }
      }
    }

    // Return the number of items we successfully added
    return addedCount;
  }

  /**
   * Shuffles array using Fisher-Yates algorithm
   * @param {Array} array The array to shuffle
   * @returns {Array} New shuffled array
   */
  shuffleArray(array) {
    // Create a copy to avoid modifying the original
    const shuffled = Array.from(array);

    // Fisher-Yates shuffle algorithm
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled;
  }

  addItemToDOM(itemKey, variantData, productData, container) {
    console.log('=== addItemToDOM called ===');
    console.log('itemKey:', itemKey);
    console.log('container:', container);
    
    const template = this.querySelector('[data-bundle-item-template]');
    console.log('template found:', template);
    if (!template || !container) {
      console.log('No template or container, returning');
      return;
    }
    
    // Now that the template structure is fixed, we can clone the bundle-item directly
    const templateBundleItem = template.querySelector('.bundle-item');
    console.log('templateBundleItem found:', templateBundleItem);
    if (!templateBundleItem) {
      console.log('No templateBundleItem found, returning');
      return;
    }
    
    const itemElement = templateBundleItem.cloneNode(true);
    console.log('cloned itemElement:', itemElement.outerHTML.substring(0, 200) + '...');
    itemElement.classList.remove('hidden');
    itemElement.setAttribute('data-item-key', itemKey);
    const imageElement = itemElement.querySelector('.bundle-item__image');
    if (imageElement) {
      if (variantData.media_link || productData.featured_image) {
        imageElement.src = variantData.media_link || productData.featured_image;
        imageElement.alt = productData.title.split(' - ')[0] || '';
        imageElement.classList.remove('hidden');
      } else {
        imageElement.classList.add('hidden');
      }
    }
    const nameElement = itemElement.querySelector('.bundle-item__name');
    if (nameElement) {
      nameElement.textContent = productData.title.split(' - ')[0];
    }
    const variantTitleElement = itemElement.querySelector('.bundle-item__variant-title');
    if (variantTitleElement && variantData.title) {
      variantTitleElement.textContent = variantData.title;
    }
    const qtyElement = itemElement.querySelector('.bundle-item__qty');
    if (
      qtyElement &&
      (this.bundleState.bundleItems[itemKey]?.quantity || this.bundleState.addOnItems[itemKey].quantity)
    ) {
      qtyElement.textContent =
        this.bundleState.bundleItems[itemKey]?.quantity || this.bundleState.addOnItems[itemKey]?.quantity;
    }
    const priceElement = itemElement.querySelector('.bundle-item__price');
    if (priceElement) {
      priceElement.innerHTML =
        variantData.bundlePrice === 0
          ? window.fsbSettings.free_label || 'Free'
          : Fsb.formatMoney(variantData.bundlePrice);
    }
    
    // Show nuts badge if product contains nuts
    const nutsBadge = itemElement.querySelector('.fsb__nuts-badge');
    
    if (nutsBadge && (productData.containsNuts || (productData.metafields && productData.metafields.custom && productData.metafields.custom.contains_nuts === true))) {
      nutsBadge.classList.remove('hidden');
    }
    
    // Add quantity selector functionality for bundle items (not add-ons)
    const quantitySelector = itemElement.querySelector('.fsb__quantity-selector');
    const isAddon = container === this.addOnItemsContainers[0] || container === this.addOnItemsContainers[1];
    
    if (quantitySelector) {
      if (isAddon) {
        // Hide quantity selector for add-on items
        quantitySelector.classList.add('hidden');
        // Show the simple quantity display for add-ons
        const simpleQty = itemElement.querySelector('.bundle-item__qty');
        if (simpleQty) {
          simpleQty.classList.remove('hidden');
        }
      } else {
        // Show quantity selector for bundle items
        quantitySelector.classList.remove('hidden');
      const decreaseBtn = quantitySelector.querySelector('.fsb__quantity-selector-decrease');
      const increaseBtn = quantitySelector.querySelector('.fsb__quantity-selector-increase');
      const quantityInput = quantitySelector.querySelector('.fsb__quantity-selector-input');
      
      if (decreaseBtn && increaseBtn && quantityInput) {
        // Set initial quantity value
        const currentQuantity = this.bundleState.bundleItems[itemKey]?.quantity || 1;
        quantityInput.value = currentQuantity;
        
        // Set initial button states
        decreaseBtn.disabled = currentQuantity <= 0;
        increaseBtn.disabled = this.bundleState.currentQuantity >= this.bundleState.bundleQuantity;
        
        // Decrease button handler
        decreaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(quantityInput.value, 10) || 1;
          if (currentValue > 0) {
            const newValue = currentValue - 1;
            quantityInput.value = newValue;
            if (newValue === 0) {
              // Remove the item when quantity reaches 0
              this.bundleState.removeBundleItem(itemKey);
            } else {
              this.updateBundleItemQuantity(itemKey, newValue);
            }
            // Sync with product cards
            this.syncQuantityWithProductCards(itemKey, newValue);
          }
        });
        
        // Increase button handler
        increaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(quantityInput.value, 10) || 1;
          const newValue = currentValue + 1;
          // Check if we can add more items to the bundle
          if (this.bundleState.currentQuantity < this.bundleState.bundleQuantity) {
            quantityInput.value = newValue;
            this.updateBundleItemQuantity(itemKey, newValue);
            // Sync with product cards
            this.syncQuantityWithProductCards(itemKey, newValue);
          }
        });
        
        // Input change handler
        quantityInput.addEventListener('change', (e) => {
          let newValue = parseInt(e.target.value, 10) || 1;
          if (newValue < 1) newValue = 1;
          
          const currentItemQuantity = this.bundleState.bundleItems[itemKey]?.quantity || 1;
          const quantityDiff = newValue - currentItemQuantity;
          
          // Calculate maximum allowed quantity
          const maxAllowed = this.bundleState.bundleQuantity - (this.bundleState.currentQuantity - currentItemQuantity);
          
          // Show message if exceeding max, but allow the input to show the entered value temporarily
          if (newValue > maxAllowed) {
            Fsb.showToast('Maximum bundle size reached');
            // Update to max allowed after a short delay to show the user their input
            setTimeout(() => {
              e.target.value = maxAllowed;
              this.updateBundleItemQuantity(itemKey, maxAllowed);
              // Sync with product cards
              this.syncQuantityWithProductCards(itemKey, maxAllowed);
            }, 100);
            return;
          }
          
          // If within limits, update normally
          e.target.value = newValue;
          this.updateBundleItemQuantity(itemKey, newValue);
          // Sync with product cards
          this.syncQuantityWithProductCards(itemKey, newValue);
        });
      }
      }
    }

    const removeButton = itemElement.querySelector('.bundle-item__remove');
    if (removeButton) {
      removeButton.addEventListener('click', () => {
        if (isAddon) {
          this.bundleState.removeAddonItem(itemKey);
        } else {
          // Remove ALL items of this variant from the bundle at once
          const bundleItem = this.bundleState.bundleItems[itemKey];
          if (bundleItem) {
            // Update current quantity by subtracting all items of this variant
            this.bundleState.currentQuantity -= bundleItem.quantity;
            // Remove the entire variant from bundle items
            delete this.bundleState.bundleItems[itemKey];
            // Notify subscribers of the change
            this.bundleState.notifySubscribers('bundleItems');
          }
        }
      });
    }

    // Check if this is an add-on item with personalization
    if (
      (container === this.addOnItemsContainers[0] || container === this.addOnItemsContainers[1]) &&
      variantData.personalization
    ) {
      if (itemElement) {
        const detailsElement = itemElement.querySelector('.bundle-item__details');

        if (detailsElement) {
          // Add View Message button after the name
          const viewMessageButton = document.createElement('button');
          viewMessageButton.className = 'fsb__button--link';
          viewMessageButton.textContent = `${window.fsbSettings.view_message_button_text}`;
          nameElement.parentNode.insertBefore(viewMessageButton, nameElement.nextSibling);

          // Add click handler to open personalization modal
          viewMessageButton.addEventListener('click', () => {
            const personalizationModal = document.querySelector('fsb-personalization');
            if (personalizationModal) {
              // Create a dummy product card with necessary data
              productData.button = viewMessageButton;
              const dummyProductCard = {
                product: productData,
                bundleState: this.bundleState,
              };

              // Show the modal with existing data
              personalizationModal.showPersonalizationWithData(
                dummyProductCard,
                variantData,
                variantData.personalization,
                itemKey
              );
            }
          });

          const personalizationElement = document.createElement('p');
          personalizationElement.className = 'bundle-item__personalization';
          // Prepend a pencil icon and truncate the message to ~30 characters
          const preview =
            variantData.personalization.length > 30
              ? variantData.personalization.substring(0, 27) + '...'
              : variantData.personalization;
          // Use the merchant-defined property name in the tooltip/title
          personalizationElement.innerHTML = `<span class="personalization-icon">✏️</span> ${preview}`;
          // If truncated, add a tooltip with the full message
          if (variantData.personalization.length > 30) {
            personalizationElement.title = variantData.personalization;
          }
          detailsElement.appendChild(personalizationElement);
        }
      }
    }

    console.log('About to append itemElement to container');
    console.log('Final itemElement HTML:', itemElement.outerHTML.substring(0, 300) + '...');
    container.appendChild(itemElement);
    console.log('Container children after append:', container.children.length);
    console.log('Last added element classes:', container.lastElementChild?.className);
    console.log('=== addItemToDOM end ===');
  }



  getAddonQuantityForVariant(variantId) {
    if (!variantId || !this.bundleState) return 0;
    
    const stringId = variantId.toString();
    let count = 0;
    
    // Count all items with this variant ID (exact match or starts with ID_)
    Object.keys(this.bundleState.addOnItems).forEach(key => {
      if (key === stringId || key.startsWith(stringId + '_')) {
        count += this.bundleState.addOnItems[key].quantity || 1;
      }
    });
    
    return count;
  }

  updateAddonQuantityFromSidebar(variantId, newQuantity) {
    if (!variantId || !this.bundleState) return;

    const currentQuantity = this.getAddonQuantityForVariant(variantId);
    
    // If quantity didn't change, don't do anything
    if (currentQuantity === newQuantity) return;

    // Update the bundle state directly
    const key = variantId.toString();
    
    // Remove current item if it exists
    if (this.bundleState.addOnItems[key]) {
      this.bundleState.addOnQuantity -= this.bundleState.addOnItems[key].quantity;
      delete this.bundleState.addOnItems[key];
    }
    
    // Add new item if quantity > 0
    if (newQuantity > 0) {
      // Try to find product data from existing cards
      let productCard = document.querySelector(`fsb-productaddon[data-variant-id="${variantId}"]`);
      
      if (!productCard) {
        productCard = document.querySelector(`fsb-productcard[data-variant-id="${variantId}"]`);
      }
      
      if (productCard && productCard.product && productCard.currentVariant) {
        const itemData = {
          price: 0, // Addon products should be free
          title: productCard.product.title.split(' - ')[0],
          variantTitle: !productCard.currentVariant.is_default ? productCard.currentVariant.title : null,
          image: productCard.currentVariant.media_link || productCard.product.featured_image,
          metafields: productCard.product.metafields,
          containsNuts: productCard.dataset.containsNuts === 'true',
          variantId: variantId,
          quantity: newQuantity
        };

        // Add the item with the new quantity
        this.bundleState.addAddonItem(key, itemData);
      }
    }
    
    // Notify subscribers of the change
    this.bundleState.notifySubscribers('addOnItems');
  }

  removeItemFromDOM(itemKey, container) {
    // If a specific container is provided, remove from just that container
    if (container) {
      const itemToRemove = container.querySelector(`[data-item-key="${itemKey}"]`);
      if (itemToRemove) {
        container.removeChild(itemToRemove);
      }
      return;
    }

    if (this.bundleState.addOnItems[itemKey]) {
      this.forEachAddonContainer((container) => {
        const itemToRemove = container.querySelector(`[data-item-key="${itemKey}"]`);
        if (itemToRemove) {
          container.removeChild(itemToRemove);
        }
      });
    } else {
      // Otherwise, it's a bundle item, remove from bundle container
      const itemToRemove = this.itemsContainer.querySelector(`[data-item-key="${itemKey}"]`);
      if (itemToRemove) {
        this.itemsContainer.removeChild(itemToRemove);
      }
    }
  }

  removeVariantFromBundle(itemKey) {
    const variantRemovedEvent = new CustomEvent('variantRemovedFromBundle', {
      detail: { itemKey },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(variantRemovedEvent);
  }

  removeAddonVariantFromBundle(variantId) {
    const addonVariantRemovedEvent = new CustomEvent('addonVariantRemoved', {
      detail: { variantId },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(addonVariantRemovedEvent);
  }

  updateAddToBundleButtons() {
    const addToBundleButtons = document.querySelectorAll('.fsb__atk-button--add');
    addToBundleButtons.forEach((button) => {
      const productCard = button.closest('fsb-productcard, fsb-productaddon');
      if (productCard) {
        productCard.checkAndUpdateButtonState(this.button);
      }
    });
  }

  removeEventListeners() {
    // Remove event listeners from all button instances
    this.addToCartButtons.forEach((button) => {
      button.removeEventListener('click', this.addBundleToCart);
    });

    this.continueButtons.forEach((button) => {
      button.removeEventListener('click', this.continueToAddOns);
    });

    this.backButtons.forEach((button) => {
      button.removeEventListener('click', this.backToProducts);
    });

    this.randomizeButtons.forEach((button) => {
      button.removeEventListener('click', this.handleRandomize);
    });
  }

  removeExcessAddOns(newMaximum = this.bundleState.addOnLimits.maximum) {
    while (this.bundleState.addOnQuantity > newMaximum) {
      const variantIds = Object.keys(this.bundleState.addOnItems);
      if (variantIds.length === 0) break;

      const lastVariantId = variantIds[variantIds.length - 1];
      this.bundleState.removeAddonItem(lastVariantId);
      this.updateAddToBundleButtons();

      // This will now remove from all addon containers
      this.removeItemFromDOM(lastVariantId);
    }
  }

  removeExcessItems() {
    const excessQuantity = this.bundleState.currentQuantity - this.bundleState.bundleQuantity;
    if (excessQuantity <= 0) return;
    
    const itemKeys = Object.keys(this.bundleState.bundleItems);
    itemKeys.sort((b, a) => this.bundleState.bundleItems[b].addedAt - this.bundleState.bundleItems[a].addedAt);
    
    let remainingToRemove = excessQuantity;
    let currentKeyIndex = itemKeys.length - 1;
    
    // Batch remove items directly from state without triggering individual notifications
    while (remainingToRemove > 0 && currentKeyIndex >= 0) {
      const itemKey = itemKeys[currentKeyIndex];
      const item = this.bundleState.bundleItems[itemKey];
      const removeFromThis = Math.min(remainingToRemove, item.quantity);
      
      // Directly modify the bundle state without calling removeBundleItem to avoid auto-sizing logic
      item.quantity -= removeFromThis;
      this.bundleState.currentQuantity -= removeFromThis;
      
      if (item.quantity === 0) {
        delete this.bundleState.bundleItems[itemKey];
      }
      
      remainingToRemove -= removeFromThis;
      currentKeyIndex--;
    }
    
    // Now trigger a single batch update of all UI components
    this.bundleState.notifySubscribers('bundleItems');
    this.bundleState.updateProgressBar();
    this.bundleState.updateAllCardCapacityStates();
    this.updateSelectionCounts();
    this.updateAddToBundleButtons();
    this.updateTotalPrice();
    this.updateButtonStates();
  }

  updateTotalPrice() {
    let bundleItemsTotal = 0;
    let addOnItemsTotal = 0;
    Object.values(this.bundleState.bundleItems).forEach((item) => {
      bundleItemsTotal += item.price * item.quantity;
    });
    Object.values(this.bundleState.addOnItems).forEach((item) => {
      if (item?.price) {
        addOnItemsTotal += item.price * item.quantity;
      }
    });

    // For dynamic pricing strategies that might need recalculation
    if (this.bundleState.priceStrategy.strategy === 'fixed_pricing') {
      const finalPrice = this.bundleState.priceStrategy.value * 100 + addOnItemsTotal;

      this.updateBundlePrice(finalPrice);
    } else if (this.bundleState.priceStrategy.strategy === 'dynamic_pricing') {
      const discountPercentage = this.bundleState.priceStrategy.value / 100;
      const originalPrice = bundleItemsTotal + addOnItemsTotal;
      const discountAmount = bundleItemsTotal * discountPercentage;
      const finalPrice = originalPrice - discountAmount;
      this.updateBundlePrice(finalPrice, originalPrice);
    } else {
      this.updateBundlePrice(bundleItemsTotal + addOnItemsTotal);
    }
  }

  updateBundlePrice(finalPrice, originalPrice = null) {
    if (!this.bundlePriceElements || this.bundlePriceElements.length === 0) return;
    
    this.bundlePriceElements.forEach(priceElement => {
      if (originalPrice) {
        priceElement.innerHTML = `
          <span class="original-price">${Fsb.formatMoney(originalPrice)}</span>
          <span class="discounted-price">${Fsb.formatMoney(finalPrice)}</span>
        `;
        priceElement.classList.add('has-discount');
      } else {
        priceElement.innerHTML = Fsb.formatMoney(finalPrice);
        priceElement.classList.remove('has-discount');
      }
    });
  }

  /**
   * Initialize accordion states based on initial content
   */
  initializeAccordionStates() {
    const bundleContainer = this.querySelector('.bundle-items-container');
    const bundleCaret = this.querySelector('.fsb__bundle-details-wrapper .icon-caret');
    const addonContainer = this.querySelector('.addon-items-container.fsb__desktop-only');
    const addonCaret = this.querySelector('.addon-items-header .icon-caret');

    const inProductsView = this.bundleState.currentView === 'products';
    const inAddonsView = this.bundleState.currentView === 'addons';

    if (!this.hasAddons) {
      // Without add-ons: always expand bundle items and hide accordions on desktop
      this.setAccordionState(bundleContainer, true);
      this.setCaretState(bundleCaret, false);

      // Hide add-on elements
      this.setAccordionState(addonContainer, false);
      this.setCaretState(addonCaret, false);
    } else {
      // With add-ons: set initial states based on current view
      this.setAccordionState(bundleContainer, inProductsView);
      this.setCaretState(bundleCaret, true);

      this.setAccordionState(addonContainer, inAddonsView);
      this.setCaretState(addonCaret, true);
    }

    this.updateAddonsVisibility();
  }

  /**
   * Updates accordion states based on content changes
   */
  updateAccordionStates() {
    // If bundle items container is expanded, update its height (only if not in unified container)
    const bundleItemsContainer = this.querySelector('.bundle-items-container');
    if (bundleItemsContainer && bundleItemsContainer.classList.contains('expanded')) {
      const isInUnified = bundleItemsContainer.closest('.unified-items-container');
      if (!isInUnified) {
        // Allow height to adjust to new content
        bundleItemsContainer.style.height = 'auto';
        const newHeight = bundleItemsContainer.scrollHeight;
        bundleItemsContainer.style.height = `${newHeight}px`;
      }
    }

    // If addon items container is expanded, update its height (only if not in unified container)
    const addonItemsContainer = this.querySelector('.addon-items-container.fsb__desktop-only');
    if (addonItemsContainer && addonItemsContainer.classList.contains('expanded')) {
      const isInUnified = addonItemsContainer.closest('.unified-items-container');
      if (!isInUnified) {
        // Allow height to adjust to new content
        addonItemsContainer.style.height = 'auto';
        const newHeight = addonItemsContainer.scrollHeight;
        addonItemsContainer.style.height = `${newHeight}px`;
      }
    }
  }

  /**
   * Updates accordions based on current view
   */
  updateAccordionsBasedOnView() {
    const currentView = this.bundleState.currentView;
    const bundleItemsContainer = this.querySelector('.bundle-items-container');
    const bundleDetailsWrapper = this.querySelector('.fsb__bundle-details-wrapper');
    const addonItemsContainer = this.querySelector('.addon-items-container.fsb__desktop-only');
    const addonHeaderContainer = this.querySelector('.addon-items-header');

    if (!bundleItemsContainer || !bundleDetailsWrapper || !addonItemsContainer || !addonHeaderContainer) {
      return;
    }

    if (currentView === 'products') {
      // Expand bundle items if not already expanded
      if (!bundleItemsContainer.classList.contains('expanded')) {
        this.toggleAccordion(bundleDetailsWrapper, bundleItemsContainer);
      }

      // Collapse addon items if expanded
      if (addonItemsContainer.classList.contains('expanded')) {
        this.toggleAccordion(addonHeaderContainer, addonItemsContainer);
      }

      // Enable bundle item interactions
      bundleItemsContainer.classList.remove('fsb--disabled');
    } else if (currentView === 'addons') {
      // Expand addon items if not already expanded
      if (!addonItemsContainer.classList.contains('expanded')) {
        this.toggleAccordion(addonHeaderContainer, addonItemsContainer);
      }

      // Keep bundle items expanded but disable interactions
      if (!bundleItemsContainer.classList.contains('expanded')) {
        this.toggleAccordion(bundleDetailsWrapper, bundleItemsContainer);
      }
      
      // Disable bundle item interactions in addon view
      bundleItemsContainer.classList.add('fsb--disabled');
    }
  }

  /**
   * Updates the display of items in the sidebar
   */
  updateItemsDisplay() {
    this.updateBundleItems();
    this.updateAddonItems();
  }

  /**
   * Updates bundle items in the sidebar
   */
  updateBundleItems() {
    if (!this.itemsContainer) return;

    // Get existing items and create a map for quick lookup
    const existingItems = this.itemsContainer.querySelectorAll('.bundle-item:not([data-bundle-item-template])');
    const existingItemsMap = new Map();
    existingItems.forEach(item => {
      const itemKey = item.getAttribute('data-item-key');
      if (itemKey) {
        existingItemsMap.set(itemKey, item);
      }
    });

    // Track which items should exist
    const currentItemKeys = new Set(Object.keys(this.bundleState.bundleItems));

    // Remove items that no longer exist in state
    existingItemsMap.forEach((element, key) => {
      if (!currentItemKeys.has(key)) {
        element.remove();
        existingItemsMap.delete(key);
      }
    });

    // Add new items (only items that don't already exist in DOM)
    Object.entries(this.bundleState.bundleItems).forEach(([itemKey, itemData]) => {
      if (!existingItemsMap.has(itemKey)) {
        // This is a new item, add it
        this.addBundleItemToDOM(itemKey, itemData);
      } else {
        // This item already exists, just update its quantity if needed
        const existingElement = existingItemsMap.get(itemKey);
        const quantityInput = existingElement.querySelector('.fsb__quantity-selector-input');
        if (quantityInput && parseInt(quantityInput.value, 10) !== itemData.quantity) {
          quantityInput.value = itemData.quantity;
        }
      }
    });

    // Show/hide placeholder
    const placeholder = this.itemsContainer.querySelector('.items-placeholder');
    if (placeholder) {
      if (Object.keys(this.bundleState.bundleItems).length === 0) {
        placeholder.removeAttribute('hidden');
      } else {
        placeholder.setAttribute('hidden', 'true');
      }
    }
  }

  /**
   * Updates addon items in the sidebar
   */
  updateAddonItems() {
    console.log('=== updateAddonItems START ===');
    console.log('addOnItems in state:', this.bundleState.addOnItems);
    console.log('Number of addon containers:', this.addOnItemsContainers?.length);
    
    this.forEachAddonContainer((container) => {
      console.log('Processing container:', container);
      
      // Clear existing addon items (except template)
      const existingItems = container.querySelectorAll('.bundle-item:not([data-bundle-item-template])');
      console.log('Clearing existing items:', existingItems.length);
      existingItems.forEach(item => item.remove());

      // Add current addon items
      console.log('Adding addon items from state...');
      Object.entries(this.bundleState.addOnItems).forEach(([itemKey, itemData]) => {
        console.log('Processing addon item:', itemKey, itemData);
        this.addAddonItemToDOM(itemKey, itemData, container);
      });

      // Show/hide placeholder
      const placeholder = container.querySelector('.items-placeholder');
      if (placeholder) {
        if (Object.keys(this.bundleState.addOnItems).length === 0) {
          placeholder.removeAttribute('hidden');
        } else {
          placeholder.setAttribute('hidden', 'true');
        }
      }
    });
    console.log('=== updateAddonItems END ===');
  }

  /**
   * Adds a bundle item to the DOM
   */
  addBundleItemToDOM(itemKey, itemData) {
    const template = this.querySelector('[data-bundle-item-template]');
    if (!template || !this.itemsContainer) return;

    // Clone ONLY the .bundle-item element, not the entire template wrapper
    const templateBundleItem = template.querySelector('.bundle-item');
    if (!templateBundleItem) return;
    
    const itemElement = templateBundleItem.cloneNode(true);
    
    // Remove template attributes and make visible
    itemElement.removeAttribute('data-bundle-item-template');
    itemElement.classList.remove('hidden');
    itemElement.setAttribute('data-item-key', itemKey);
    itemElement.setAttribute('data-variant-id', itemData.variantId);

    // Populate item data
    this.populateItemElement(itemElement, itemData);

    // Add quantity selector functionality for bundle items
    this.setupQuantitySelector(itemElement, itemKey, false);

    // Add remove button functionality
    this.setupRemoveButton(itemElement, itemKey, false);

    // Append ONLY the bundle-item element, not any wrapper
    this.itemsContainer.appendChild(itemElement);
  }

  /**
   * Adds an addon item to the DOM
   */
  addAddonItemToDOM(itemKey, itemData, container) {
    const template = this.querySelector('[data-bundle-item-template]');
    if (!template || !container) return;

    // Validate that we have meaningful data
    if (!itemData || (!itemData.title && !itemData.image && !itemData.variantId)) {
      console.warn('Skipping addon item with insufficient data:', itemData);
      return;
    }

    // Clone only the inner bundle-item element, not the entire container
    const templateBundleItem = template.querySelector('.bundle-item');
    if (!templateBundleItem) {
      console.error('Bundle item element not found in template');
      return;
    }

    const itemElement = templateBundleItem.cloneNode(true);
    
    // Set up the cloned element
    itemElement.setAttribute('data-item-key', itemKey);
    itemElement.setAttribute('data-variant-id', itemData.variantId || itemKey);

    // Populate the element with data
    this.populateItemElement(itemElement, itemData);

    // Add quantity selector functionality for addon items
    this.setupQuantitySelector(itemElement, itemKey, true);

    // Add remove button functionality
    this.setupRemoveButton(itemElement, itemKey, true);

    // Append the bundle-item directly to the container (no wrapper div)
    container.appendChild(itemElement);
  }

  /**
   * Populates an item element with data
   */
  populateItemElement(itemElement, itemData) {
    // Set image
    const imageElement = itemElement.querySelector('.bundle-item__image');
    if (imageElement && itemData.image) {
      imageElement.src = itemData.image;
      imageElement.alt = itemData.title || '';
    }

    // Set name - always set something
    const nameElement = itemElement.querySelector('.bundle-item__name');
    if (nameElement) {
      nameElement.textContent = itemData.title || 'Addon Item';
    }

    // Set variant title
    const variantTitleElement = itemElement.querySelector('.bundle-item__variant-title');
    if (variantTitleElement && itemData.variantTitle) {
      variantTitleElement.textContent = itemData.variantTitle;
    }

    // Set price - always set something
    const priceElement = itemElement.querySelector('.bundle-item__price');
    if (priceElement) {
      const price = itemData.price !== undefined ? itemData.price : 0;
      priceElement.innerHTML = price === 0 
        ? (window.fsbSettings?.free_label || 'Free')
        : Fsb.formatMoney(price);
    }

    // Set quantity in input
    const quantityInput = itemElement.querySelector('.fsb__quantity-selector-input');
    if (quantityInput) {
      quantityInput.value = itemData.quantity || 1;
    }

    // Show nuts badge if applicable
    const nutsBadge = itemElement.querySelector('.fsb__nuts-badge');
    if (nutsBadge && itemData.containsNuts) {
      nutsBadge.classList.remove('hidden');
    }
  }

  /**
   * Sets up quantity selector functionality
   */
  setupQuantitySelector(itemElement, itemKey, isAddon) {
    const quantitySelector = itemElement.querySelector('.fsb__quantity-selector');
    if (!quantitySelector) return;

    const decreaseBtn = quantitySelector.querySelector('.fsb__quantity-selector-decrease');
    const increaseBtn = quantitySelector.querySelector('.fsb__quantity-selector-increase');
    const quantityInput = quantitySelector.querySelector('.fsb__quantity-selector-input');

    if (!decreaseBtn || !increaseBtn || !quantityInput) return;

    // Decrease button handler
    decreaseBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const currentValue = parseInt(quantityInput.value, 10) || 1;
      const newValue = Math.max(0, currentValue - 1);
      
      if (newValue === 0) {
        // Remove item completely
        if (isAddon) {
          delete this.bundleState.addOnItems[itemKey];
          this.bundleState.addOnQuantity -= currentValue;
        } else {
          delete this.bundleState.bundleItems[itemKey];
          this.bundleState.currentQuantity -= currentValue;
        }
      } else {
        // Update quantity
        quantityInput.value = newValue;
        if (isAddon) {
          this.bundleState.addOnItems[itemKey].quantity = newValue;
          this.bundleState.addOnQuantity += (newValue - currentValue);
        } else {
          this.bundleState.bundleItems[itemKey].quantity = newValue;
          this.bundleState.currentQuantity += (newValue - currentValue);
        }
      }

      // Sync with product cards
      this.syncWithProductCards(itemKey, newValue);

      // Notify subscribers
      this.bundleState.notifySubscribers(isAddon ? 'addOnItems' : 'bundleItems');
    });

    // Increase button handler
    increaseBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const currentValue = parseInt(quantityInput.value, 10) || 1;
      
      // Check capacity limits
      if (isAddon && this.bundleState.addOnQuantity >= this.bundleState.addOnLimits.maximum) {
        Fsb.showToast('Addon limit reached');
        return;
      }
      if (!isAddon && this.bundleState.currentQuantity >= this.bundleState.bundleQuantity) {
        Fsb.showToast('Bundle limit reached');
        return;
      }

      const newValue = currentValue + 1;
      quantityInput.value = newValue;

      // Update quantity
      if (isAddon) {
        this.bundleState.addOnItems[itemKey].quantity = newValue;
        this.bundleState.addOnQuantity += 1;
      } else {
        this.bundleState.bundleItems[itemKey].quantity = newValue;
        this.bundleState.currentQuantity += 1;
      }

      // Sync with product cards
      this.syncWithProductCards(itemKey, newValue);

      // Notify subscribers
      this.bundleState.notifySubscribers(isAddon ? 'addOnItems' : 'bundleItems');
    });

    // Input change handler
    quantityInput.addEventListener('change', (e) => {
      let newValue = parseInt(e.target.value, 10);
      if (isNaN(newValue) || newValue < 0) {
        newValue = 0;
      }

      const currentQuantity = isAddon 
        ? this.bundleState.addOnItems[itemKey]?.quantity || 1
        : this.bundleState.bundleItems[itemKey]?.quantity || 1;

      if (newValue === 0) {
        // Remove item completely
        if (isAddon) {
          delete this.bundleState.addOnItems[itemKey];
          this.bundleState.addOnQuantity -= currentQuantity;
        } else {
          delete this.bundleState.bundleItems[itemKey];
          this.bundleState.currentQuantity -= currentQuantity;
        }
      } else {
        // Check capacity limits
        const quantityDiff = newValue - currentQuantity;
        if (isAddon && (this.bundleState.addOnQuantity + quantityDiff) > this.bundleState.addOnLimits.maximum) {
          const maxAllowed = this.bundleState.addOnLimits.maximum - (this.bundleState.addOnQuantity - currentQuantity);
          newValue = Math.max(0, maxAllowed);
          e.target.value = newValue;
          Fsb.showToast('Addon limit reached');
        }
        if (!isAddon && (this.bundleState.currentQuantity + quantityDiff) > this.bundleState.bundleQuantity) {
          const maxAllowed = this.bundleState.bundleQuantity - (this.bundleState.currentQuantity - currentQuantity);
          newValue = Math.max(0, maxAllowed);
          e.target.value = newValue;
          Fsb.showToast('Bundle limit reached');
        }

        // Update quantity
        if (isAddon) {
          this.bundleState.addOnItems[itemKey].quantity = newValue;
          this.bundleState.addOnQuantity += (newValue - currentQuantity);
        } else {
          this.bundleState.bundleItems[itemKey].quantity = newValue;
          this.bundleState.currentQuantity += (newValue - currentQuantity);
        }
      }

      // Sync with product cards
      this.syncWithProductCards(itemKey, newValue);

      // Notify subscribers
      this.bundleState.notifySubscribers(isAddon ? 'addOnItems' : 'bundleItems');
    });
  }

  /**
   * Sets up remove button functionality
   */
  setupRemoveButton(itemElement, itemKey, isAddon) {
    const removeButton = itemElement.querySelector('.bundle-item__remove');
    if (!removeButton) return;

    removeButton.addEventListener('click', (e) => {
      e.preventDefault();
      
      const currentQuantity = isAddon 
        ? this.bundleState.addOnItems[itemKey]?.quantity || 1
        : this.bundleState.bundleItems[itemKey]?.quantity || 1;

      // Remove item completely
      if (isAddon) {
        delete this.bundleState.addOnItems[itemKey];
        this.bundleState.addOnQuantity -= currentQuantity;
      } else {
        delete this.bundleState.bundleItems[itemKey];
        this.bundleState.currentQuantity -= currentQuantity;
      }

      // Sync with product cards
      this.syncWithProductCards(itemKey, 0);

      // Notify subscribers
      this.bundleState.notifySubscribers(isAddon ? 'addOnItems' : 'bundleItems');
    });
  }

  /**
   * Syncs quantity changes with product cards
   */
  syncWithProductCards(variantId, newQuantity) {
    // Find product cards with matching variant ID
    const productCards = document.querySelectorAll(`fsb-productcard[data-variant-id="${variantId}"], fsb-productaddon[data-variant-id="${variantId}"]`);
    
    productCards.forEach(card => {
      const quantityInput = card.querySelector('.fsb__quantity-selector-input');
      if (quantityInput && parseInt(quantityInput.value, 10) !== newQuantity) {
        quantityInput.value = newQuantity;
        
        // Update button states on the product card
        const container = quantityInput.closest('.fsb__quantity-selector');
        if (container) {
          const decreaseBtn = container.querySelector('.fsb__quantity-selector-decrease');
          const increaseBtn = container.querySelector('.fsb__quantity-selector-increase');
          
          if (decreaseBtn) {
            decreaseBtn.disabled = false; // Always allow decrease for removal
          }
          
          if (increaseBtn) {
            const cardIsAddon = card.getAttribute('is') === 'fsb-productaddon' || card.dataset.category === 'add-ons';
            if (cardIsAddon) {
              increaseBtn.disabled = this.bundleState.addOnQuantity >= this.bundleState.addOnLimits.maximum;
            } else {
              increaseBtn.disabled = this.bundleState.currentQuantity >= this.bundleState.bundleQuantity;
            }
          }
        }
        
        // Show/hide quantity selector based on quantity
        const quantitySelector = card.querySelector('.fsb__quantity-selector');
        if (quantitySelector) {
          if (newQuantity === 0) {
            quantitySelector.classList.add('hidden');
          } else {
            quantitySelector.classList.remove('hidden');
          }
        }
      }
    });
  }

  /**
   * Updates UI based on current view (products vs addons)
   */
  updateViewBasedUI() {
    const currentView = this.bundleState.currentView || 'products';
    
    // Reset all product card quantities to show appropriate values for current view
    this.resetProductCardQuantities(currentView);
    
    // Update sidebar quantity selectors
    this.updateSidebarQuantitySelectors();
    
    // Update selection count visibility
    document.querySelectorAll('.bundle-selection-count.products').forEach((el) => {
      el.style.display = currentView === 'products' ? 'block' : 'none';
    });
    document.querySelectorAll('.bundle-selection-count.addons').forEach((el) => {
      el.style.display = currentView === 'addons' ? 'block' : 'none';
    });
    
    // Always update addon dots when view changes
    this.updateAddonDots();
  }

  /**
   * Updates accordion states based on current view
   */
  updateAccordionsBasedOnView() {
    const currentView = this.bundleState.currentView || 'products';
    
    // Handle accordion states based on view
    if (currentView === 'addons') {
      // In addon view, expand addon section and collapse bundle section
      const addonContainer = this.querySelector('.addon-items-container');
      const bundleContainer = this.querySelector('.bundle-items-container');
      
      if (addonContainer && !addonContainer.classList.contains('expanded')) {
        this.setAccordionState(addonContainer, true);
      }
      if (bundleContainer && bundleContainer.classList.contains('expanded')) {
        this.setAccordionState(bundleContainer, false);
      }
    } else {
      // In products view, expand bundle section
      const bundleContainer = this.querySelector('.bundle-items-container');
      if (bundleContainer && !bundleContainer.classList.contains('expanded')) {
        this.setAccordionState(bundleContainer, true);
      }
    }
  }



  /**
   * Updates sidebar quantity selectors to disable + buttons when max is reached
   */
  updateSidebarQuantitySelectors() {
    // Get all sidebar quantity selectors
    const sidebarQuantitySelectors = this.querySelectorAll('.fsb__bundle .bundle-item .fsb__quantity-selector');
    
    sidebarQuantitySelectors.forEach(selector => {
      const increaseBtn = selector.querySelector('.fsb__quantity-selector-increase');
      const decreaseBtn = selector.querySelector('.fsb__quantity-selector-decrease');
      const quantityInput = selector.querySelector('.fsb__quantity-selector-input');
      
      if (!increaseBtn || !quantityInput) return;
      
      // Find the parent bundle item to determine if it's an addon or bundle item
      const bundleItem = selector.closest('.bundle-item');
      if (!bundleItem) return;
      
      // Check if this is an addon item by looking at the parent container
      const isInAddonContainer = bundleItem.closest('.addon-items-container') !== null;
      const isAddonItem = isInAddonContainer || bundleItem.classList.contains('addon-item') || 
                         bundleItem.querySelector('[data-category="add-ons"]') ||
                         bundleItem.querySelector('fsb-productaddon');
      
      // Get the current quantity for this specific item
      const currentQuantity = parseInt(quantityInput.value, 10) || 0;
      
      // Show the quantity selector
      selector.classList.remove('hidden');
      
      if (isAddonItem) {
        // For addon items, disable + button when this specific addon item reaches the addon limit
        const maxCount = this.bundleState.addOnLimits.maximum || 0;
        increaseBtn.disabled = currentQuantity >= maxCount;
      } else {
        // For bundle items, disable + button when this specific bundle item reaches the bundle quantity
        const maxCount = this.bundleState.bundleQuantity || 0;
        increaseBtn.disabled = currentQuantity >= maxCount;
      }
      
      // Always allow decrease button for removal
      if (decreaseBtn) {
        decreaseBtn.disabled = false;
      }
    });
  }

  /**
   * Resets product card quantities to show appropriate values for current view
   */
  resetProductCardQuantities(currentView) {
    // Get all product cards
    const allProductCards = document.querySelectorAll('fsb-productcard, fsb-productaddon');
    
    allProductCards.forEach(card => {
      const variantId = card.getAttribute('data-variant-id');
      if (!variantId) return;
      
      const quantityInput = card.querySelector('.fsb__quantity-selector-input');
      const quantitySelector = card.querySelector('.fsb__quantity-selector');
      
      if (!quantityInput || !quantitySelector) return;
      
      let appropriateQuantity = 0;
      const cardIsAddon = card.getAttribute('is') === 'fsb-productaddon' || card.dataset.category === 'add-ons';
      
      if (currentView === 'addons') {
        // In addon view, show only addon quantities
        if (cardIsAddon) {
          appropriateQuantity = this.bundleState.addOnItems[variantId]?.quantity || 0;
        } else {
          // Hide bundle item quantities in addon view
          appropriateQuantity = 0;
        }
      } else {
        // In products view, show appropriate quantities
        if (cardIsAddon) {
          // Hide addon quantities in products view
          appropriateQuantity = 0;
        } else {
          appropriateQuantity = this.bundleState.bundleItems[variantId]?.quantity || 0;
        }
      }
      
      // Update the quantity input
      quantityInput.value = appropriateQuantity;
      
      // Show/hide quantity selector based on quantity
      if (appropriateQuantity === 0) {
        quantitySelector.classList.add('hidden');
      } else {
        quantitySelector.classList.remove('hidden');
      }
      
      // Update button states
      const container = quantityInput.closest('.fsb__quantity-selector');
      if (container) {
        const decreaseBtn = container.querySelector('.fsb__quantity-selector-decrease');
        const increaseBtn = container.querySelector('.fsb__quantity-selector-increase');
        
        if (decreaseBtn) {
          decreaseBtn.disabled = false; // Always allow decrease for removal
        }
        
        if (increaseBtn) {
          if (cardIsAddon) {
            increaseBtn.disabled = this.bundleState.addOnQuantity >= this.bundleState.addOnLimits.maximum;
          } else {
            increaseBtn.disabled = this.bundleState.currentQuantity >= this.bundleState.bundleQuantity;
          }
        }
      }
    });
  }

  /**
   * Switches to addon view
   */
  continueToAddOns() {
    if (!this.hasAddons) return;
    
    // Update bundle state view
    this.bundleState.currentView = 'addons';
    
    // Hide all individual tabs and show the addon container
    const allTabs = document.querySelectorAll('fsb-tab');
    const addonContainer = document.querySelector('.fsb__addons-container');
    
    allTabs.forEach(tab => {
      tab.classList.add('hidden');
    });
    
    if (addonContainer) {
      addonContainer.classList.remove('hidden');
    }
    
    // Disable dropdown selector during addon stage
    this.setDropdownState(false);
    
    // Update button visibility
    this.updateButtonStates();
    
    // Notify subscribers of view change
    this.bundleState.notifySubscribers('view_changed');
  }

  /**
   * Switches back to products view
   */
  backToProducts() {
    // Update bundle state view
    this.bundleState.currentView = 'products';
    
    // Show all individual tabs and hide the addon container
    const allTabs = document.querySelectorAll('fsb-tab');
    const addonContainer = document.querySelector('.fsb__addons-container');
    
    allTabs.forEach((tab, index) => {
      tab.classList.remove('hidden');
      // Restore active state to first tab
      if (index === 0) {
        tab.classList.add('active');
      } else {
        tab.classList.remove('active');
      }
    });
    
    if (addonContainer) {
      addonContainer.classList.add('hidden');
    }
    
    // Re-enable dropdown selector when back to bundle stage
    this.setDropdownState(true);
    
    // Update button visibility
    this.updateButtonStates();
    
    // Notify subscribers of view change
    this.bundleState.notifySubscribers('view_changed');
  }

  /**
   * Adds event listeners for buttons and interactions
   */
  addEventListeners() {
    // Continue buttons
    this.continueButtons.forEach((button) => {
      button.addEventListener('click', () => this.continueToAddOns());
    });

    // Back buttons
    this.backButtons.forEach((button) => {
      button.addEventListener('click', () => this.backToProducts());
    });

    // Add to cart buttons
    this.addToCartButtons.forEach((button) => {
      button.addEventListener('click', () => this.addBundleToCart());
    });

    // Bundle option change events
    if (this.bundleOptions) {
      this.bundleOptions.addEventListener('bundleOptionChange', this.bundleOptionChangeHandler.bind(this));
    }

    this.syncCookieTypeSelectors();
  }

  /**
   * Removes event listeners
   */
  removeEventListeners() {
    // Event listeners are automatically removed when elements are removed from DOM
    // This method is here for completeness and future cleanup if needed
  }
}

customElements.define('fsb-aside', FsbAside);

/**
 * Popover UI component
 * Handles dropdown menus and tooltips
 */
class FsbPopover extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.trigger = null;
    this.content = null;
  }

  connectedCallback() {
    this.trigger = this.querySelector('.fsb__popover-trigger');
    this.content = this.querySelector('.fsb__popover-content');
    if (!this.trigger || !this.content) {
      console.error('FsbPopover: Missing trigger or content element');
      return;
    }
    this.trigger.addEventListener('click', () => this.toggle());
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener('click', this.handleOutsideClick);
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.content.classList.add('fsb__popover-content--active');
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.isOpen = false;
    this.content.classList.remove('fsb__popover-content--active');
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  handleOutsideClick(event) {
    if (this.isOpen && !this.contains(event.target)) {
      this.close();
    }
  }
}

customElements.define('fsb-popover', FsbPopover);

/**
 * Base modal dialog component
 * Handles basic modal functionality
 */
class FsbModalDialog extends HTMLElement {
  constructor() {
    super();
    this.closer = this.querySelector('[id^="ModalClose"]');
    this.content = this.querySelector('.fsb__modal-content');
    this.openedBy = null;
    this.previouslyFocusedElement = null;

    // Bind methods
    this.handleKeyup = this.handleKeyup.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.hide = this.hide.bind(this);
  }

  connectedCallback() {
    if (!this.moved) {
      this.moved = true;
      document.body.appendChild(this);
    }
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  setupEventListeners() {
    this.closer?.addEventListener('click', this.hide);
    this.addEventListener('keyup', this.handleKeyup);
    this.addEventListener('click', this.handleOutsideClick);
  }

  removeEventListeners() {
    this.closer?.removeEventListener('click', this.hide);
    this.removeEventListener('keyup', this.handleKeyup);
    this.removeEventListener('click', this.handleOutsideClick);
  }

  handleKeyup(event) {
    if (event.code.toUpperCase() === 'ESCAPE') this.hide();
  }

  handleOutsideClick(event) {
    if (event.target === this) this.hide();
  }

  show(opener) {
    this.openedBy = opener;
    document.body.classList.add('overflow-hidden');
    this.previouslyFocusedElement = document.activeElement;

    this.setAttribute('open', '');
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');

    this.setAttribute('tabindex', '-1');
    this.focus();

    queueMicrotask(() => {
      this.content.scrollTop = 0;
      this.trapFocus();
    });
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');
    this.restoreFocus();
    this.cleanup();
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
  }

  trapFocus() {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length) {
      this.firstFocusable = focusableElements[0];
      this.lastFocusable = focusableElements[focusableElements.length - 1];
      this.firstFocusable.focus();
      this.addEventListener('keydown', this.handleTabKey);
    }
  }

  handleTabKey(e) {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === this.firstFocusable) {
        e.preventDefault();
        this.lastFocusable.focus();
      }
    } else {
      if (document.activeElement === this.lastFocusable) {
        e.preventDefault();
        this.firstFocusable.focus();
      }
    }
  }

  restoreFocus() {
    if (this.openedBy) {
      this.openedBy.button?.focus();
    }
  }

  cleanup() {
    this.content.innerHTML = '';
    this.removeEventListener('keydown', this.handleTabKey);
    this.removeAttribute('role');
    this.removeAttribute('aria-modal');
    this.firstFocusable = null;
    this.lastFocusable = null;
  }
}

customElements.define('fsb-modaldialog', FsbModalDialog);

/**
 * Product modal dialog component
 * Extends base modal with product-specific functionality
 */
class FsbProductModal extends FsbModalDialog {
  constructor() {
    super();
    this.currentProduct = null;
    this.variantSelector = null;
    this.mediaGallery = null;
    this.productCard = null;
    this.buttonObserver = null;
    this.variantObserver = null;

    // Bind additional methods
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.addEventListener('variantChange', this.handleVariantChange);
    this.handleProductImageClick = this.handleProductImageClick.bind(this);
    this.handleProductImageKeydown = this.handleProductImageKeydown.bind(this);
    this.delegatedClickHandler = this.delegatedClickHandler.bind(this);
    this.delegatedKeydownHandler = this.delegatedKeydownHandler.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.setupProductImageListeners();

    const productContainer = document.querySelector('.fsb__tabs');
    if (productContainer) {
      productContainer.addEventListener('click', this.delegatedClickHandler);
      productContainer.addEventListener('keydown', this.delegatedKeydownHandler);
    }
  }

  delegatedClickHandler(event) {
    const productImage = event.target.closest('.fsb__category-grid__product-card .fsb__product-card__image');
    if (productImage) {
      // Use the original handler logic with the found element
      this.handleProductImageClick(event, productImage);
    }
  }

  delegatedKeydownHandler(event) {
    const productImage = event.target.closest('.fsb__category-grid__product-card .fsb__product-card__image');
    if (productImage && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.handleProductImageClick(event, productImage);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleProductImageClick);
  }

  setupProductImageListeners() {
    document.querySelectorAll('.fsb__category-grid__product-card .fsb__product-card__image').forEach((image) => {
      image.removeEventListener('click', this.handleProductImageClick);
      image.addEventListener('click', this.handleProductImageClick);

      // Make image focusable with keyboard
      if (!image.hasAttribute('tabindex')) {
        image.setAttribute('tabindex', '0');
      }

      // Add keyboard event listener
      image.removeEventListener('keydown', this.handleProductImageKeydown);
      image.addEventListener('keydown', this.handleProductImageKeydown);
    });
  }

  removeProductImageListeners() {
    document.querySelectorAll('.fsb__category-grid__product-card .fsb__product-card__image').forEach((image) => {
      image.removeEventListener('click', this.handleProductImageClick);
      image.removeEventListener('keydown', this.handleProductImageKeydown);
    });
  }

  handleProductImageClick(event, productImage = event.currentTarget) {
    event.preventDefault();
    event.stopPropagation();

    if (!productImage) return;

    const productCard = productImage.closest('.fsb__category-grid__product-card');
    if (!productCard) return;

    if (event.target.closest('fsb-variantselector') || event.target.closest('.fsb__atk-button')) {
      return;
    }

    this.showProduct(productImage);
  }

  showProduct(opener) {
    this.productCard = opener.closest('.fsb__category-grid__product-card');

    try {
      this.currentProduct = JSON.parse(this.productCard.dataset.product);
      super.show(this.productCard);
      this.setupModalContent();
      this.setupStateSynchronization();
    } catch (error) {
      console.error('Error setting up product modal:', error);
      this.hide();
    }
  }

  setupModalContent() {
    if (!this.content || !this.currentProduct) return;

    const modalHTML = this.createModalHTML();
    this.content.innerHTML = modalHTML;

    this.setupMediaGallery();
    this.setupVariantSelector();
    this.setupAddToCartButton();
  }

  createModalHTML() {
    const { title, description } = this.currentProduct;
    const { price } = this.productCard.variantSelector.currentVariant;

    return `
      <div class="fsb__modal-layout">
        <div class="fsb__modal-media" id="modal-media"></div>
        <div class="fsb__modal-details">
          <h2 class="fsb__modal-title">${title}</h2>
          <div class="fsb__modal-price">${Fsb.formatMoney(price)}</div>
          <div class="fsb__modal-variant-container fsb__category-grid__product-card"></div>
          <div class="fsb__modal-description">${description || ''}</div>
          <div class="fsb__modal-actions fsb__category-grid__product-card">
          ${Array.from(this.productCard.querySelectorAll('.fsb__atk-button'))
            .map((button) => button.outerHTML)
            .join('')}
          </div>
        </div>
      </div>
    `;
  }

  setupStateSynchronization() {
    const originalSelector = this.productCard.querySelector('fsb-variantselector');
    const modalSelector = this.content.querySelector('fsb-variantselector');

    if (originalSelector && modalSelector) {
      this.setupVariantSync(originalSelector, modalSelector);
    }

    const modalButtons = this.content.querySelectorAll('.fsb__atk-button');
    const originalButtons = this.productCard.querySelectorAll('.fsb__atk-button');

    if (modalButtons.length && originalButtons.length) {
      this.setupButtonSync(originalButtons, modalButtons);
    }
  }

  setupVariantSync(originalSelector, modalSelector) {
    this.syncVariantState(originalSelector, modalSelector);

    modalSelector.addEventListener('change', (event) => {
      const { name, value } = event.target;
      const originalSelect = originalSelector.querySelector(`select[name="${name}"]`);
      if (originalSelect && originalSelect.value !== value) {
        originalSelect.value = value;
        originalSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    this.variantObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const originalSelect = mutation.target;
          const modalSelect = modalSelector.querySelector(`select[name="${originalSelect.name}"]`);
          if (modalSelect && modalSelect.value !== originalSelect.value) {
            modalSelect.value = originalSelect.value;
            modalSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    });

    originalSelector.querySelectorAll('select').forEach((select) => {
      this.variantObserver.observe(select, {
        attributes: true,
        attributeFilter: ['value'],
      });
    });
  }

  syncVariantState(originalSelector, modalSelector) {
    modalSelector.querySelectorAll('select').forEach((select) => {
      const originalSelect = originalSelector.querySelector(`select[name="${select.name}"]`);
      if (originalSelect) {
        select.value = originalSelect.value;
      }
    });

    modalSelector.querySelectorAll('input[type="radio"]').forEach((input) => {
      const originalInput = originalSelector.querySelector(
        `input[type="radio"][name="${input.name}"][value="${input.value}"]`
      );
      if (originalInput) {
        input.checked = originalInput.checked;
      }
    });
  }

  setupButtonSync(originalButtons, modalButtons) {
    const syncButtonStates = () => {
      modalButtons.forEach((modalButton, index) => {
        const originalButton = originalButtons[index];
        if (originalButton) {
          modalButton.disabled = originalButton.disabled;
          modalButton.classList = originalButton.classList;
          modalButton.textContent = originalButton.textContent;
          modalButton.dataset.available = originalButton.dataset.available;
        }
      });
    };

    syncButtonStates();

    this.buttonObserver = new MutationObserver(syncButtonStates);

    originalButtons.forEach((button) => {
      this.buttonObserver.observe(button, {
        attributes: true,
        attributeFilter: ['disabled', 'class', 'data-available'],
        childList: true,
        characterData: true,
      });
    });

    modalButtons.forEach((modalButton, index) => {
      modalButton.addEventListener('click', () => {
        const originalButton = originalButtons[index];
        if (originalButton && !originalButton.disabled) {
          originalButton.click();
          this.hide();
        }
      });
    });
  }

  handleVariantChange(event) {
    const { variant } = event.detail;
    if (!variant) return;

    const priceElement = this.content.querySelector('.fsb__modal-price');
    if (priceElement && variant.price) {
      priceElement.innerHTML = Fsb.formatMoney(variant.price);
    }

    if (variant.featured_image) {
      this.mediaGallery?.updateMainImage(variant.featured_image);
    }
  }

  setupMediaGallery() {
    const mediaContainer = this.content.querySelector('#modal-media');
    if (!mediaContainer) return;

    this.mediaGallery = new FsbProductGallery(
      mediaContainer,
      this.currentProduct.media || [],
      this.currentProduct.featured_image
    );
    this.mediaGallery.init();
  }

  setupVariantSelector() {
    const container = this.content.querySelector('.fsb__modal-variant-container');
    if (!container) return;

    const originalSelector = this.openedBy
      ?.closest('.fsb__category-grid__product-card')
      ?.querySelector('fsb-variantselector');

    if (originalSelector) {
      this.variantSelector = this.cloneVariantSelector(originalSelector);
      container.appendChild(this.variantSelector);

      this.variantSelector.addEventListener('variantChange', (event) => {
        const originalEvent = new CustomEvent('variantChange', {
          detail: event.detail,
          bubbles: true,
        });
        originalSelector.dispatchEvent(originalEvent);
      });
    }
  }

  cloneVariantSelector(original) {
    const clone = document.createElement('fsb-variantselector');
    clone.innerHTML = original.innerHTML;

    Array.from(original.attributes).forEach((attr) => {
      if (attr.name !== 'id') {
        clone.setAttribute(attr.name, attr.value);
      }
    });

    clone.querySelectorAll('[id]').forEach((element) => {
      element.id = `modal-${element.id}`;
    });

    return clone;
  }

  setupAddToCartButton() {
    const button = this.content.querySelector('.fsb__atk-button');
    if (!button) return;

    button.addEventListener('click', () => {
      const originalButton = this.openedBy
        ?.closest('.fsb__category-grid__product-card')
        ?.querySelector(`.fsb__atk-button[data-action="${button.dataset.action}"`);

      if (originalButton) {
        originalButton.click();
        this.hide();
      }
    });
  }

  cleanup() {
    this.buttonObserver?.disconnect();
    this.variantObserver?.disconnect();
    this.buttonObserver = null;
    this.variantObserver = null;
    this.currentProduct = null;
    this.productCard = null;
    this.mediaGallery?.destroy();
    this.mediaGallery = null;
    this.variantSelector = null;
    super.cleanup();
  }

  handleProductImageKeydown(event) {
    if (fsbAdditionalSettings.show_popup === true) {
      if (event.key === 'Enter' || event.key === ' ') {
        const handleProductImageClick = document.querySelector('fsb-product-modal').handleProductImageClick;
        event.preventDefault();
        handleProductImageClick(event);
      }
    }
  }
}

customElements.define('fsb-product-modal', FsbProductModal);

/**
 * Product gallery handler for modal
 * Manages media display and thumbnails
 */
class FsbProductGallery {
  constructor(container, mediaItems = [], fallbackImage = null) {
    this.container = container;
    this.mediaItems = mediaItems;
    this.fallbackImage = fallbackImage;
    this.currentIndex = 0;
  }

  init() {
    this.container.innerHTML = this.createGalleryHTML();

    if (this.mediaItems.length > 0) {
      this.setupMediaElements();
    } else if (this.fallbackImage) {
      this.setupFallbackImage();
    }

    this.setupEventListeners();
  }

  createGalleryHTML() {
    return `
      <div class="fsb__gallery-main"></div>
      ${this.mediaItems.length > 1 ? '<div class="fsb__gallery-thumbs"></div>' : ''}
    `;
  }

  setupMediaElements() {
    const mainContainer = this.container.querySelector('.fsb__gallery-main');
    const thumbsContainer = this.container.querySelector('.fsb__gallery-thumbs');

    // Setup main image
    const mainImage = this.createImage(this.mediaItems[0].src);
    mainContainer.appendChild(mainImage);

    // Setup thumbnails if multiple images
    if (thumbsContainer && this.mediaItems.length > 1) {
      const fragment = document.createDocumentFragment();

      for (const [index, media] of this.mediaItems.entries()) {
        const thumb = this.createThumbnail(media, index === 0);
        fragment.appendChild(thumb);
      }

      thumbsContainer.appendChild(fragment);
    }
  }

  createImage(src, alt = '') {
    const img = new Image();
    img.src = src;
    img.alt = alt;
    img.className = 'fsb__gallery-image';
    return img;
  }

  createThumbnail(media, isActive = false) {
    const thumb = document.createElement('button');
    thumb.className = `fsb__gallery-thumb ${isActive ? 'is-active' : ''}`;
    thumb.setAttribute('aria-label', `View ${media.alt || 'product image'}`);

    const img = this.createImage(`${media.src}&width=100`, media.alt);
    thumb.appendChild(img);

    return thumb;
  }

  setupFallbackImage() {
    const mainContainer = this.container.querySelector('.fsb__gallery-main');
    const img = new Image();
    img.src = this.fallbackImage;
    img.alt = '';
    img.className = 'fsb__gallery-image';
    mainContainer.appendChild(img);
  }

  setupEventListeners() {
    const thumbsContainer = this.container.querySelector('.fsb__gallery-thumbs');
    if (!thumbsContainer) return;

    thumbsContainer.addEventListener('click', (e) => {
      const thumb = e.target.closest('.fsb__gallery-thumb');
      if (!thumb) return;

      const index = Array.from(thumbsContainer.children).indexOf(thumb);
      this.updateMainImage(index);
    });
  }

  updateMainImage(index) {
    if (index === this.currentIndex) return;

    const mainImage = this.container.querySelector('.fsb__gallery-main img');
    if (!mainImage) return;

    const media = this.mediaItems[index];
    mainImage.src = media.src;
    mainImage.alt = media.alt || '';

    // Update thumbnails
    const thumbs = this.container.querySelectorAll('.fsb__gallery-thumb');
    thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
    });

    this.currentIndex = index;
  }

  destroy() {
    this.container.innerHTML = '';
  }
}

customElements.define('fsb-product-gallery', FsbProductGallery);

/**
 * Personalization modal component
 * Extends base modal with personalization functionality
 */
class FsbPersonalizationModal extends FsbModalDialog {
  constructor() {
    super();
    this.currentVariant = null;
    this.productCard = null;
    this.maxLength = window.fsbSettings.personalization_char;

    // Set up modal event listeners
    this.setupEventListeners();
  }

  showPersonalization(productCard, variant) {
    this.productCard = productCard;
    this.currentVariant = variant;
    this.setupModalContent();
    super.show(productCard);
  }

  setupModalContent() {
    if (!this.content || !this.currentVariant) return;

    const modalHTML = this.createModalHTML();
    this.content.innerHTML = modalHTML;
    this.setupFormEventListeners();

    // Focus the textarea after content is set up
    queueMicrotask(() => {
      const messageArea = this.content.querySelector('#addon-message');
      if (messageArea) messageArea.focus();
    });
  }

  setupFormEventListeners() {
    const messageArea = this.content.querySelector('#addon-message');
    const charCount = this.content.querySelector('#char-count');
    const addButton = this.content.querySelector('#add-personalization');
    const cancelButton = this.content.querySelector('#cancel-personalization');

    messageArea?.addEventListener('input', (e) => {
      const count = e.target.value.length;
      if (charCount) charCount.textContent = count;
    });

    addButton?.addEventListener('click', () => {
      const message = messageArea?.value || '';
      this.handleAddToBundle(message);
    });

    cancelButton?.addEventListener('click', () => {
      this.hide();
    });
  }

  createModalHTML() {
    const { title } = this.productCard.product;
    const imageUrl = this.currentVariant.media_link || this.productCard.product.featured_image;
    const displayTitle =
      this.currentVariant.title && !this.currentVariant.is_default ? `${title} - ${this.currentVariant.title}` : title;

    return `
    <div class="fsb__personalization-modal">
      <h2 class="fsb__personalization-title">${displayTitle}</h2>
      <div class="fsb__personalization-layout">
        ${
          imageUrl
            ? `<div class="fsb__personalization-image-container">
                  <img src="${imageUrl}" alt="${title}" class="fsb__personalization-image">
                </div>`
            : ''
        }
        <div class="fsb__personalization-content">
          <div class="fsb__personalization-form">
            <label for="addon-message" class="fsb__personalization-label">
              ${window.fsbSettings.personalization_title}
            </label>
            <textarea 
              id="addon-message" 
              class="fsb__personalization-textarea" 
              placeholder="${window.fsbSettings.personalization_placeholder}"
              maxlength="${this.maxLength}"
            ></textarea>
            <div class="fsb__personalization-counter">
              <span id="char-count">0</span>/${this.maxLength}
            </div>
          </div>
          <div class="fsb__personalization-actions">
            <button type="button" class="fsb__button fsb__button-inverted" id="cancel-personalization">
              ${window.fsbSettings.personalization_cancel}
            </button>
            <button type="button" class="fsb__button" id="add-personalization">
              ${window.fsbSettings.personalization_add}
            </button>
          </div>
        </div>
      </div>
    </div>
    `;
  }

  handleAddToBundle(message) {
    if (!this.currentVariant?.id || !this.productCard?.bundleState) return;

    if (this.itemKey) {
      // Update existing item
      const addonItems = this.productCard.bundleState.addOnItems;
      if (addonItems[this.itemKey]) {
        addonItems[this.itemKey].personalization = message.trim();
        this.productCard.bundleState.notifySubscribers('addOnItems');
      }
    } else {
      // Add new item
      if (this.productCard.bundleState.addOnQuantity >= this.productCard.bundleState.addOnLimits.maximum) {
        const notification = document.createElement('div');
        notification.className = 'fsb__notification';
        notification.textContent = window.fsbSettings.personalization_max_error;
        this.productCard.appendChild(notification);
        setTimeout(() => notification.remove(), 3000);
        return;
      }

      this.productCard.bundleState.addAddonItem(this.currentVariant.id, {
        price: 0, // Addon products should be free
        title: this.productCard.product.title,
        variantTitle: !this.currentVariant.is_default ? this.currentVariant.title : null,
        image: this.currentVariant.media_link || this.productCard.product.featured_image,
        personalization: message.trim(),
        metafields: this.productCard.product.metafields, // Include metafields
        containsNuts: this.productCard.dataset.containsNuts === 'true', // Add nuts info from data attribute
      });
    }

    this.hide();
  }

  hide() {
    super.hide();
  }

  cleanup() {
    this.currentVariant = null;
    this.productCard = null;
    this.itemKey = null;
    super.cleanup();
  }

  showPersonalizationWithData(productCard, variant, existingMessage, itemKey) {
    this.productCard = productCard;
    this.currentVariant = variant;
    this.itemKey = itemKey;
    this.setupModalContent();
    super.show(productCard);

    // Pre-fill the message
    const messageArea = this.content.querySelector('#addon-message');
    const charCount = this.content.querySelector('#char-count');
    if (messageArea && existingMessage) {
      messageArea.value = existingMessage;
      if (charCount) {
        charCount.textContent = existingMessage.length;
      }
    }

    // Update the add button text to indicate editing
    const addButton = this.content.querySelector('#add-personalization');
    if (addButton) {
      // Use "Update" + property name to clearly indicate what's being updated
      addButton.textContent =
        window.fsbSettings.personalization_add || `${window.fsbSettings.personalization_property_name}`;
    }
  }
}

customElements.define('fsb-personalization', FsbPersonalizationModal);